/**
 * CAD Viewer - Main controller for CAD functionality in CoandaView
 * Wraps existing RenderingEngine and integrates with existing collaboration infrastructure
 *
 * Key Design Principles:
 * - REUSE RenderingEngine (don't create new scene/camera/renderer)
 * - REUSE WebSocket collaboration (section locks, presence, state sync)
 * - REUSE Utils, Auth, and other existing infrastructure
 * - Modular: Each CAD feature is a separate method
 */

class CADViewer {
  constructor(viewer) {
    // Dependency injection: Reuse existing viewer instance
    this.viewer = viewer;
    this.engine = viewer.engine;          // REUSE existing RenderingEngine
    this.scene = this.engine.scene;       // Same Three.js scene
    this.camera = this.engine.camera;     // Same camera
    this.controls = this.engine.controls; // Same controls

    // CAD-specific state
    this.document = null;                 // Current CADDocument
    this.currentMode = 'view';            // 'view' or 'cad'
    this.activeSketch = null;             // Currently editing sketch
    this.activeTool = null;               // Active sketch tool
    this.selectedFeatureId = null;        // Selected feature in tree

    // Geometry kernel
    this.kernelLoader = getGeometryKernelLoader();
    this.kernel = null;

    // CAD objects in scene (Map<featureId, THREE.Mesh>)
    this.cadObjects = new Map();

    // Part registry (Map<partId, Part>)
    this.parts = new Map();

    // UI elements (cached for performance)
    this.elements = {};

    // Performance settings
    this.tessellationQuality = 0.1;  // Lower = finer mesh

    // Sketch engine
    this.sketchEngine = null;

    // Surface modeler
    this.surfaceModeler = null;

    // Profile detector
    this.profileDetector = null;

    // Gear designer
    this.gearDesigner = null;

    // History manager for undo/redo
    this.historyManager = null;

    // Feature manager for centralized feature lifecycle
    this.featureManager = null;

    // Selection
    this.selectedFeatureId = null;

    // Initialize
    this.initialize();
  }

  /**
   * Initialize CAD viewer
   */
  async initialize() {
    console.log('[CADViewer] Initializing...');

    try {
      // Initialize sketch engine if available
      if (typeof SketchEngine !== 'undefined') {
        this.sketchEngine = new SketchEngine(this);
        console.log('[CADViewer] Sketch engine initialized');
      }

      // Initialize surface modeler if available
      if (typeof SurfaceModeler !== 'undefined') {
        this.surfaceModeler = new SurfaceModeler(this);
        console.log('[CADViewer] Surface modeler initialized');
      }

      // Initialize sheet metal workbench if available
      if (typeof SheetMetalWorkbench !== 'undefined') {
        this.sheetMetal = new SheetMetalWorkbench(this);
        console.log('[CADViewer] Sheet metal workbench initialized');
      }

      // Initialize profile detector if available
      if (typeof ProfileDetector !== 'undefined') {
        this.profileDetector = new ProfileDetector();
        console.log('[CADViewer] Profile detector initialized');
      }

      // Initialize gear designer if available
      if (typeof GearDesigner !== 'undefined') {
        this.gearDesigner = new GearDesigner(this);
        console.log('[CADViewer] Gear designer initialized');
      }

      // Initialize history manager for undo/redo
      if (typeof HistoryManager !== 'undefined') {
        this.historyManager = new HistoryManager(this, {
          maxHistorySize: 50,
          enabled: true
        });
        console.log('[CADViewer] History manager initialized');
      }

      // Initialize feature manager (centralized feature lifecycle management)
      if (typeof FeatureManager !== 'undefined') {
        this.featureManager = new FeatureManager(this);
        console.log('[CADViewer] Feature manager initialized - Parametric updates enabled');
      }

      // Initialize AI command executor (must be after featureManager)
      if (typeof CADCommandExecutor !== 'undefined') {
        window.cadCommandExecutor = new CADCommandExecutor(this);
        console.log('[CADViewer] AI Command Executor initialized');
      }

      // Initialize AI chatbot (must be after command executor)
      if (typeof CADChatBot !== 'undefined') {
        this.aiChatBot = new CADChatBot(this);
        console.log('[CADViewer] AI ChatBot initialized');
      }

      // Wire up tool buttons
      this.wireUpToolButtons();

      // Set up keyboard shortcuts
      this.setupKeyboardShortcuts();

      console.log('[CADViewer] Ready (kernel will load on demand)');

    } catch (error) {
      console.error('[CADViewer] Initialization error:', error);
    }
  }

  /**
   * Wire up sketch tool buttons
   */
  wireUpToolButtons() {
    // Sketch tools
    const toolButtons = document.querySelectorAll('.cad-tool-btn[data-tool]');
    toolButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const tool = btn.dataset.tool;
        this.activateSketchTool(tool);

        // Update active state
        toolButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });

    // Sketch/Rotate mode toggle
    document.getElementById('btnToggleMode')?.addEventListener('click', () => {
      if (this.sketchEngine) {
        this.sketchEngine.toggleCanvasInteraction();
      }
    });

    // Snap mode toggle
    document.getElementById('btnToggleSnap')?.addEventListener('click', () => {
      if (this.sketchEngine) {
        this.sketchEngine.toggleSnapMode();
      }
    });

    // Construction mode toggle
    document.getElementById('btnToggleConstruction')?.addEventListener('click', () => {
      if (this.sketchEngine) {
        this.sketchEngine.toggleConstructionMode();
      }
    });

    // New Sketch button
    document.getElementById('btnNewSketch')?.addEventListener('click', () => {
      this.showSketchPlaneModal();
    });

    // Primitive buttons
    document.getElementById('cadPrimBox')?.addEventListener('click', () => {
      this.createPrimitive('box');
    });

    document.getElementById('cadPrimSphere')?.addEventListener('click', () => {
      this.createPrimitive('sphere');
    });

    document.getElementById('cadPrimCylinder')?.addEventListener('click', () => {
      this.createPrimitive('cylinder');
    });

    document.getElementById('cadPrimCone')?.addEventListener('click', () => {
      this.createPrimitive('cone');
    });

    document.getElementById('cadPrimTorus')?.addEventListener('click', () => {
      this.createPrimitive('torus');
    });

    document.getElementById('cadPrimWedge')?.addEventListener('click', () => {
      this.createPrimitive('wedge');
    });

    // 3D operation buttons
    document.getElementById('cadOpExtrude')?.addEventListener('click', () => {
      this.performExtrude();
    });

    document.getElementById('cadOpPocket')?.addEventListener('click', () => {
      this.performPocket();
    });

    document.getElementById('cadOpRevolve')?.addEventListener('click', () => {
      this.performRevolve();
    });

    document.getElementById('cadOpGroove')?.addEventListener('click', () => {
      this.performGroove();
    });

    document.getElementById('cadOpHole')?.addEventListener('click', () => {
      this.performHole();
    });

    document.getElementById('cadOpSweep')?.addEventListener('click', () => {
      this.performSweep();
    });

    document.getElementById('cadOpLoft')?.addEventListener('click', () => {
      this.performLoft();
    });

    document.getElementById('cadOpFillet')?.addEventListener('click', () => {
      this.performFillet();
    });

    document.getElementById('cadOpChamfer')?.addEventListener('click', () => {
      this.performChamfer();
    });

    document.getElementById('cadOpShell')?.addEventListener('click', () => {
      this.performShell();
    });

    document.getElementById('cadOpLinearPattern')?.addEventListener('click', () => {
      this.performLinearPattern();
    });

    document.getElementById('cadOpCircularPattern')?.addEventListener('click', () => {
      this.performCircularPattern();
    });

    document.getElementById('cadOpMirror')?.addEventListener('click', () => {
      this.performMirror();
    });

    // Assembly buttons
    document.getElementById('cadAssemblyNew')?.addEventListener('click', () => {
      const name = prompt('Assembly name:', 'Assembly1');
      if (name) this.createAssembly(name);
    });

    document.getElementById('cadMateFastened')?.addEventListener('click', () => {
      Utils.notify('Select two components, then click Fastened to create mate', 'info');
      // TODO: Implement component selection
    });

    document.getElementById('cadMateRevolute')?.addEventListener('click', () => {
      Utils.notify('Select two components, then click Revolute to create mate', 'info');
      // TODO: Implement component selection
    });

    document.getElementById('cadMateSlider')?.addEventListener('click', () => {
      Utils.notify('Select two components, then click Slider to create mate', 'info');
      // TODO: Implement component selection
    });

    document.getElementById('cadMateCoincident')?.addEventListener('click', () => {
      Utils.notify('Select two components, then click Coincident to create mate', 'info');
      // TODO: Implement component selection
    });

    document.getElementById('cadAssemblySimulate')?.addEventListener('click', () => {
      if (this.document && this.document.assemblies.length > 0) {
        this.simulateAssemblyMotion(this.document.assemblies[0].id);
      } else {
        Utils.notify('Create an assembly first', 'warning');
      }
    });

    // Sheet Metal buttons
    document.getElementById('sheetMetalBaseFlange')?.addEventListener('click', () => {
      this.createSheetMetalBaseFlange();
    });

    document.getElementById('sheetMetalWall')?.addEventListener('click', () => {
      this.createSheetMetalWall();
    });

    document.getElementById('sheetMetalFlange')?.addEventListener('click', () => {
      this.createSheetMetalFlange();
    });

    document.getElementById('sheetMetalHem')?.addEventListener('click', () => {
      this.createSheetMetalHem();
    });

    document.getElementById('sheetMetalJunction')?.addEventListener('click', () => {
      this.createSheetMetalJunction();
    });

    document.getElementById('sheetMetalBend')?.addEventListener('click', () => {
      this.createSheetMetalBend();
    });

    document.getElementById('sheetMetalCornerRelief')?.addEventListener('click', () => {
      this.addSheetMetalCornerRelief();
    });

    document.getElementById('sheetMetalUnfold')?.addEventListener('click', () => {
      this.unfoldSheetMetalPart();
    });

    document.getElementById('sheetMetalFold')?.addEventListener('click', () => {
      this.foldSheetMetalPart();
    });

    document.getElementById('sheetMetalExportFlat')?.addEventListener('click', () => {
      this.exportSheetMetalFlatPattern();
    });

    // Sheet Metal parameter inputs
    document.getElementById('sheetMetalThickness')?.addEventListener('change', (e) => {
      this.updateSheetMetalParameter('thickness', parseFloat(e.target.value));
    });

    document.getElementById('sheetMetalBendRadius')?.addEventListener('change', (e) => {
      this.updateSheetMetalParameter('bendRadius', parseFloat(e.target.value));
    });

    document.getElementById('sheetMetalKFactor')?.addEventListener('change', (e) => {
      this.updateSheetMetalParameter('kFactor', parseFloat(e.target.value));
    });

    document.getElementById('sheetMetalBendAngle')?.addEventListener('change', (e) => {
      this.updateSheetMetalParameter('bendAngle', parseFloat(e.target.value));
    });

    document.getElementById('sheetMetalFlangeLength')?.addEventListener('change', (e) => {
      this.updateSheetMetalParameter('flangeLength', parseFloat(e.target.value));
    });

    document.getElementById('sheetMetalReliefType')?.addEventListener('change', (e) => {
      this.updateSheetMetalParameter('reliefType', e.target.value);
    });

    // Datum buttons
    document.getElementById('cadDatumPlane')?.addEventListener('click', () => {
      this.createDatumPlane();
    });

    document.getElementById('cadDatumAxis')?.addEventListener('click', () => {
      this.createDatumAxis();
    });

    document.getElementById('cadDatumPoint')?.addEventListener('click', () => {
      this.createDatumPoint();
    });

    document.getElementById('cadLCS')?.addEventListener('click', () => {
      this.createLocalCoordinateSystem();
    });

    // Measurement buttons
    document.getElementById('cadMeasureDistance')?.addEventListener('click', () => {
      this.measureDistance();
    });

    document.getElementById('cadMeasureAngle')?.addEventListener('click', () => {
      this.measureAngle();
    });

    document.getElementById('cadMeasureArea')?.addEventListener('click', () => {
      this.measureArea();
    });

    document.getElementById('cadMeasureVolume')?.addEventListener('click', () => {
      this.measureVolume();
    });

    document.getElementById('cadMeasureCOM')?.addEventListener('click', () => {
      this.measureCenterOfMass();
    });

    // Import buttons
    document.getElementById('cadImportSTEP')?.addEventListener('click', () => {
      this.importSTEP();  // Reuses existing DataLoader.loadSTEP()
    });

    // Export buttons
    document.getElementById('cadExportSTL')?.addEventListener('click', () => {
      this.exportSTL();
    });

    document.getElementById('cadExportOBJ')?.addEventListener('click', () => {
      this.exportOBJ();
    });

    document.getElementById('cadExportGLTF')?.addEventListener('click', () => {
      this.exportGLTF();
    });

    // Surface modeling buttons
    document.getElementById('cadSurfaceExtrude')?.addEventListener('click', () => {
      this.surfaceExtrudeOperation();
    });

    document.getElementById('cadSurfaceRevolve')?.addEventListener('click', () => {
      this.surfaceRevolveOperation();
    });

    document.getElementById('cadSurfaceLoft')?.addEventListener('click', () => {
      this.surfaceLoftOperation();
    });

    document.getElementById('cadSurfaceSweep')?.addEventListener('click', () => {
      this.surfaceSweepOperation();
    });

    document.getElementById('cadSurfaceOffset')?.addEventListener('click', () => {
      this.surfaceOffsetOperation();
    });

    document.getElementById('cadSurfaceRuled')?.addEventListener('click', () => {
      this.surfaceRuledOperation();
    });

    document.getElementById('cadSurfaceFill')?.addEventListener('click', () => {
      this.surfaceFillOperation();
    });

    document.getElementById('cadSurfaceTrim')?.addEventListener('click', () => {
      this.surfaceTrimOperation();
    });

    document.getElementById('cadSurfaceExtend')?.addEventListener('click', () => {
      this.surfaceExtendOperation();
    });

    document.getElementById('cadSurfaceBlend')?.addEventListener('click', () => {
      this.surfaceBlendOperation();
    });

    // Feature tree interactions
    this.wireUpFeatureTree();

    // Constraint buttons
    this.wireUpConstraintButtons();

    // Dimension buttons
    this.wireUpDimensionButtons();

    // Undo/Redo buttons
    this.wireUpHistoryButtons();

    console.log('[CADViewer] Tool buttons wired up');
  }

  /**
   * Wire up undo/redo buttons
   */
  wireUpHistoryButtons() {
    const undoBtn = document.getElementById('btnUndo');
    const redoBtn = document.getElementById('btnRedo');

    if (undoBtn) {
      undoBtn.addEventListener('click', () => {
        if (this.historyManager) {
          this.historyManager.undo();
        }
      });
    }

    if (redoBtn) {
      redoBtn.addEventListener('click', () => {
        if (this.historyManager) {
          this.historyManager.redo();
        }
      });
    }
  }

  /**
   * Set up keyboard shortcuts for CAD operations
   */
  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (event) => {
      // Only handle shortcuts when CAD mode is active
      if (!this.document) return;

      // Check if user is typing in an input field
      const target = event.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Ctrl+Z - Undo
      if (event.ctrlKey && !event.shiftKey && event.key === 'z') {
        event.preventDefault();
        if (this.historyManager) {
          this.historyManager.undo();
        }
        return;
      }

      // Ctrl+Y - Redo (Windows/Linux)
      if (event.ctrlKey && !event.shiftKey && event.key === 'y') {
        event.preventDefault();
        if (this.historyManager) {
          this.historyManager.redo();
        }
        return;
      }

      // Ctrl+Shift+Z - Redo (Mac/Alternative)
      if (event.ctrlKey && event.shiftKey && event.key === 'Z') {
        event.preventDefault();
        if (this.historyManager) {
          this.historyManager.redo();
        }
        return;
      }

      // Cmd+Z - Undo (Mac)
      if (event.metaKey && !event.shiftKey && event.key === 'z') {
        event.preventDefault();
        if (this.historyManager) {
          this.historyManager.undo();
        }
        return;
      }

      // Cmd+Shift+Z - Redo (Mac)
      if (event.metaKey && event.shiftKey && event.key === 'z') {
        event.preventDefault();
        if (this.historyManager) {
          this.historyManager.redo();
        }
        return;
      }
    });

    console.log('[CADViewer] Keyboard shortcuts initialized');
  }

  /**
   * Wire up constraint buttons
   */
  wireUpConstraintButtons() {
    const constraintButtons = document.querySelectorAll('[data-constraint]');
    constraintButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const constraintType = btn.dataset.constraint;
        this.applyConstraint(constraintType);
      });
    });
  }

  /**
   * Wire up dimension buttons
   */
  wireUpDimensionButtons() {
    const dimensionButtons = document.querySelectorAll('[data-dimension]');
    dimensionButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const dimensionType = btn.dataset.dimension;
        this.applyDimension(dimensionType);
      });
    });
  }

  /**
   * Apply geometric constraint to selected entities
   */
  applyConstraint(constraintType) {
    if (!this.sketchEngine || !this.sketchEngine.activeSketch) {
      Utils.notify('Start a sketch first', 'warning');
      return;
    }

    // Get selected entities (for now, use last 1-2 entities)
    const entities = this.sketchEngine.entities;
    if (entities.length === 0) {
      Utils.notify('Draw entities first', 'warning');
      return;
    }

    // Create constraint
    const constraint = {
      id: Utils.generateId(),
      type: constraintType,
      entities: [],
      parameters: {}
    };

    // Determine required entities based on constraint type
    switch (constraintType) {
      case 'horizontal':
      case 'vertical':
      case 'fix':
        // Single entity constraint
        constraint.entities = [entities[entities.length - 1].id];
        break;

      case 'parallel':
      case 'perpendicular':
      case 'equal':
      case 'tangent':
        // Two entity constraint
        if (entities.length < 2) {
          Utils.notify('Need at least 2 entities', 'warning');
          return;
        }
        constraint.entities = [
          entities[entities.length - 2].id,
          entities[entities.length - 1].id
        ];
        break;

      case 'coincident':
      case 'concentric':
      case 'midpoint':
        // Point-to-point or center-to-center constraint
        if (entities.length < 2) {
          Utils.notify('Need at least 2 entities', 'warning');
          return;
        }
        constraint.entities = [
          entities[entities.length - 2].id,
          entities[entities.length - 1].id
        ];
        break;

      default:
        constraint.entities = [entities[entities.length - 1].id];
    }

    // Add constraint to active sketch
    if (!this.sketchEngine.activeSketch.constraints) {
      this.sketchEngine.activeSketch.constraints = [];
    }
    this.sketchEngine.activeSketch.constraints.push(constraint);

    // Apply constraint using constraint solver
    if (typeof ConstraintSolver !== 'undefined') {
      const solver = new ConstraintSolver(entities, [constraint]);
      solver.solve();
    }

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_constraint',
        name: `Add ${constraintType} constraint`,
        sketchId: this.sketchEngine.activeSketch.id,
        constraintId: constraint.id,
        constraint: JSON.parse(JSON.stringify(constraint))
      });
    }

    Utils.notify(`Applied ${constraintType} constraint`, 'success');
    console.log('[CADViewer] Applied constraint:', constraint);
  }

  /**
   * Apply dimensional constraint
   */
  applyDimension(dimensionType) {
    if (!this.sketchEngine || !this.sketchEngine.activeSketch) {
      Utils.notify('Start a sketch first', 'warning');
      return;
    }

    const entities = this.sketchEngine.entities;
    if (entities.length === 0) {
      Utils.notify('Draw entities first', 'warning');
      return;
    }

    let value = null;
    let dimension = null;

    switch (dimensionType) {
      case 'linear':
        value = prompt('Enter distance:', '1.0');
        if (!value) return;

        dimension = {
          id: Utils.generateId(),
          type: 'linear',
          entities: [entities[entities.length - 1].id],
          value: parseFloat(value)
        };
        break;

      case 'angular':
        value = prompt('Enter angle (degrees):', '90');
        if (!value) return;

        if (entities.length < 2) {
          Utils.notify('Need 2 lines for angular dimension', 'warning');
          return;
        }

        dimension = {
          id: Utils.generateId(),
          type: 'angular',
          entities: [
            entities[entities.length - 2].id,
            entities[entities.length - 1].id
          ],
          value: parseFloat(value)
        };
        break;

      case 'radius':
        value = prompt('Enter radius:', '0.5');
        if (!value) return;

        const entity = entities[entities.length - 1];
        if (entity.type !== 'circle' && entity.type !== 'arc') {
          Utils.notify('Select a circle or arc', 'warning');
          return;
        }

        dimension = {
          id: Utils.generateId(),
          type: 'radius',
          entities: [entity.id],
          value: parseFloat(value)
        };

        // Update entity radius directly
        entity.radius = parseFloat(value);
        this.sketchEngine.render();
        break;
    }

    if (dimension) {
      if (!this.sketchEngine.activeSketch.dimensions) {
        this.sketchEngine.activeSketch.dimensions = [];
      }
      this.sketchEngine.activeSketch.dimensions.push(dimension);

      // Record in history
      if (this.historyManager) {
        this.historyManager.recordOperation({
          type: 'add_dimension',
          name: `Add ${dimensionType} dimension`,
          sketchId: this.sketchEngine.activeSketch.id,
          dimensionId: dimension.id,
          dimension: JSON.parse(JSON.stringify(dimension))
        });
      }

      Utils.notify(`Applied ${dimensionType} dimension`, 'success');
      console.log('[CADViewer] Applied dimension:', dimension);
    }
  }

  /**
   * Wire up feature tree interactions
   */
  wireUpFeatureTree() {
    const featureTree = document.getElementById('cadFeatureTree');
    if (!featureTree) return;

    // Click to select feature
    featureTree.addEventListener('click', (e) => {
      const featureItem = e.target.closest('.cad-feature-item');
      if (!featureItem) return;

      const featureId = featureItem.dataset.featureId;
      if (featureId === 'origin') return;

      this.selectFeature(featureId);
    });

    // Right-click for context menu
    featureTree.addEventListener('contextmenu', (e) => {
      const featureItem = e.target.closest('.cad-feature-item');
      if (!featureItem) return;

      e.preventDefault();
      const featureId = featureItem.dataset.featureId;
      this.showFeatureContextMenu(featureId, e.clientX, e.clientY);
    });
  }

  /**
   * Select a feature
   */
  selectFeature(featureId) {
    this.selectedFeatureId = featureId;

    // Update UI
    document.querySelectorAll('.cad-feature-item').forEach(item => {
      item.classList.toggle('active', item.dataset.featureId === featureId);
    });

    console.log('[CADViewer] Selected feature:', featureId);
  }

  /**
   * Show feature context menu
   */
  showFeatureContextMenu(featureId, x, y) {
    // Simple confirm dialog for now - can be enhanced with proper context menu
    const feature = this.document?.getFeature(featureId);
    if (!feature) return;

    const action = confirm(`Delete ${feature.name || feature.type}?`);
    if (action) {
      this.deleteFeature(featureId);
    }
  }

  /**
   * Delete a feature
   */
  deleteFeature(featureId) {
    if (!this.document) return;

    console.log('[CADViewer] Deleting feature:', featureId);

    // Use FeatureManager if available for consistent removal
    if (this.featureManager) {
      return this.featureManager.removeFeature(featureId);
    }

    // Fallback: manual deletion
    console.log('[CADViewer] cadObjects before delete:', this.cadObjects.size);

    const feature = this.document.getFeature(featureId);
    const deletedMesh = this.cadObjects.get(featureId);
    const deletedFeature = feature ? JSON.parse(JSON.stringify(feature)) : null;
    const deletedGeometry = deletedMesh ? this.historyManager?._serializeGeometry(deletedMesh) : null;

    this.document.deleteFeature(featureId);

    const mesh = this.cadObjects.get(featureId);
    if (mesh) {
      console.log('[CADViewer] Found mesh to delete:', mesh.userData);
      this.scene.remove(mesh);

      // Dispose geometry and materials
      if (mesh.geometry) {
        mesh.geometry.dispose();
      }
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(m => m.dispose());
        } else {
          mesh.material.dispose();
        }
      }

      this.cadObjects.delete(featureId);
      console.log('[CADViewer] Mesh removed, cadObjects after:', this.cadObjects.size);
    } else {
      console.warn('[CADViewer] No mesh found for feature:', featureId);
      console.log('[CADViewer] Available mesh IDs:', Array.from(this.cadObjects.keys()));
    }

    // Update feature tree
    this.updateFeatureTree();

    // Record in history
    if (this.historyManager && deletedFeature) {
      this.historyManager.recordOperation({
        type: 'delete_feature',
        name: `Delete ${deletedFeature.name}`,
        featureId: featureId,
        deletedFeature: deletedFeature,
        deletedGeometry: deletedGeometry
      });
    }

    Utils.notify('Feature deleted', 'success');
  }

  /**
   * Update feature tree display
   */
  updateFeatureTree() {
    const tree = document.getElementById('cadFeatureTree');
    const emptyMsg = document.getElementById('cadFeatureTreeEmpty');
    if (!tree) return;

    const features = this.document?.features || [];

    // ALWAYS clear existing items first (before checking if empty!)
    tree.querySelectorAll('.cad-feature-item:not([data-feature-id="origin"])').forEach(el => el.remove());

    // Show/hide empty message
    if (features.length === 0) {
      if (emptyMsg) emptyMsg.style.display = 'block';
      return;
    }

    if (emptyMsg) emptyMsg.style.display = 'none';

    // Add feature items
    features.forEach(feature => {
      const item = this.createFeatureTreeItem(feature);
      tree.appendChild(item);
    });
  }

  /**
   * Create feature tree item
   */
  createFeatureTreeItem(feature) {
    const div = document.createElement('div');
    div.className = 'cad-feature-item';
    div.dataset.featureId = feature.id;

    const icon = this.getFeatureIcon(feature.type);

    // Add Edit button for sketches
    const editButton = feature.type === 'sketch' ? `
      <button class="feature-btn" data-action="edit" title="Edit Sketch" style="padding: 2px; background: transparent; border: none; cursor: pointer; color: rgba(0,122,255,0.8);">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
        </svg>
      </button>
    ` : '';

    div.innerHTML = `
      <div class="feature-header">
        ${icon}
        <span class="feature-name">${feature.name || feature.type}</span>
        <div class="feature-actions" style="display: flex; gap: 4px; margin-left: auto;">
          ${editButton}
          <button class="feature-btn" data-action="visibility" title="Show/Hide" style="padding: 2px; background: transparent; border: none; cursor: pointer; color: rgba(255,255,255,0.6);">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
          </button>
          <button class="feature-btn" data-action="add" title="Add (Union)" style="padding: 2px; background: transparent; border: none; cursor: pointer; color: rgba(0,255,0,0.6);">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
          <button class="feature-btn" data-action="cut" title="Cut (Subtract)" style="padding: 2px; background: transparent; border: none; cursor: pointer; color: rgba(255,165,0,0.6);">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
          <button class="feature-btn" data-action="remove" title="Remove" style="padding: 2px; background: transparent; border: none; cursor: pointer; color: rgba(255,0,0,0.6);">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Add sketch entities as children if this is a sketch
    if (feature.type === 'sketch' && feature.parameters.entities && feature.parameters.entities.length > 0) {
      console.log('[CADViewer] Adding', feature.parameters.entities.length, 'entities to tree for', feature.name);

      const childrenDiv = document.createElement('div');
      childrenDiv.className = 'feature-children';
      childrenDiv.style.cssText = 'padding-left: 24px; margin-top: 4px; display: block;';  // Show by default!
      childrenDiv.dataset.expanded = 'true';  // Start expanded

      feature.parameters.entities.forEach((entity, index) => {
        const entityItem = document.createElement('div');
        entityItem.className = 'cad-feature-item cad-entity-item';
        entityItem.style.cssText = 'font-size: 11px; opacity: 0.9;';
        entityItem.dataset.entityId = entity.id;
        entityItem.innerHTML = `
          <div class="feature-header" style="padding: 4px 6px;">
            <svg class="feature-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="2"></circle>
            </svg>
            <span class="feature-name">${entity.type}${index + 1}</span>
            <div class="feature-actions" style="display: flex; gap: 2px; margin-left: auto;">
              <button class="feature-btn" data-action="visibility" data-entity-id="${entity.id}" title="Show/Hide" style="padding: 1px; background: transparent; border: none; cursor: pointer; color: rgba(255,255,255,0.5); font-size: 10px;">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </button>
              <button class="feature-btn" data-action="add" data-entity-id="${entity.id}" title="Add" style="padding: 1px; background: transparent; border: none; cursor: pointer; color: rgba(0,255,0,0.5); font-size: 10px;">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
              <button class="feature-btn" data-action="cut" data-entity-id="${entity.id}" title="Cut" style="padding: 1px; background: transparent; border: none; cursor: pointer; color: rgba(255,165,0,0.5); font-size: 10px;">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
              <button class="feature-btn" data-action="remove" data-entity-id="${entity.id}" title="Remove" style="padding: 1px; background: transparent; border: none; cursor: pointer; color: rgba(255,0,0,0.5); font-size: 10px;">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
        `;
        childrenDiv.appendChild(entityItem);
      });

      div.appendChild(childrenDiv);

      // Add expand/collapse toggle
      const header = div.querySelector('.feature-header');
      const expandIcon = document.createElement('span');
      expandIcon.className = 'expand-icon';
      expandIcon.innerHTML = '▼';  // Down arrow since expanded by default
      expandIcon.style.cssText = 'font-size: 10px; margin-right: 6px; cursor: pointer; transition: transform 0.2s; transform: rotate(90deg);';  // Rotated
      expandIcon.title = 'Click to collapse/expand entities';
      header.insertBefore(expandIcon, header.firstChild);

      expandIcon.addEventListener('click', (e) => {
        e.stopPropagation();
        const isExpanded = childrenDiv.dataset.expanded === 'true';
        childrenDiv.dataset.expanded = !isExpanded;
        childrenDiv.style.display = isExpanded ? 'none' : 'block';
        expandIcon.style.transform = isExpanded ? 'rotate(0deg)' : 'rotate(90deg)';
        expandIcon.innerHTML = isExpanded ? '▶' : '▼';
      });
    }

    // Wire up action buttons
    this.wireUpFeatureActions(div, feature);

    return div;
  }

  /**
   * Wire up feature action buttons
   */
  wireUpFeatureActions(featureItem, feature) {
    const buttons = featureItem.querySelectorAll('[data-action]');

    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const action = btn.dataset.action;

        switch (action) {
          case 'edit':
            if (feature.type === 'sketch') {
              this.editSketch(feature.id);
            }
            break;
          case 'visibility':
            this.toggleFeatureVisibility(feature.id);
            break;
          case 'add':
            this.performBooleanAdd(feature.id);
            break;
          case 'cut':
            this.performBooleanCut(feature.id);
            break;
          case 'remove':
            if (confirm(`Remove ${feature.name || feature.type}?`)) {
              this.deleteFeature(feature.id);
            }
            break;
        }
      });
    });
  }

  /**
   * Toggle feature visibility (no recompute)
   */
  toggleFeatureVisibility(featureId) {
    const mesh = this.cadObjects.get(featureId);
    if (mesh) {
      mesh.visible = !mesh.visible;

      // Update button appearance
      const item = document.querySelector(`[data-feature-id="${featureId}"]`);
      const visBtn = item?.querySelector('[data-action="visibility"]');
      if (visBtn) {
        visBtn.style.opacity = mesh.visible ? '1.0' : '0.3';
      }

      console.log('[CADViewer] Toggled visibility:', featureId, mesh.visible);
    }
  }

  /**
   * Perform boolean add (union) - recomputes geometry
   */
  performBooleanAdd(featureId) {
    const feature = this.document?.getFeature(featureId);
    if (!feature) return;

    const targetMesh = this.cadObjects.get(featureId);
    if (!targetMesh) {
      Utils.notify('No geometry to add', 'warning');
      return;
    }

    // Find previous solid features to union with
    const previousFeatures = [];
    for (const f of this.document.features) {
      if (f.id === featureId) break;
      if (f.type === 'extrude' || f.type === 'revolve') {
        previousFeatures.push(f);
      }
    }

    if (previousFeatures.length === 0) {
      Utils.notify('No previous features to add to', 'info');
      return;
    }

    Utils.notify('Performing boolean union...', 'info');

    try {
      let resultCSG = CSG.fromMesh(targetMesh);

      // Union with previous features
      for (const prevFeature of previousFeatures) {
        const prevMesh = this.cadObjects.get(prevFeature.id);
        if (prevMesh) {
          const prevCSG = CSG.fromMesh(prevMesh);
          resultCSG = resultCSG.union(prevCSG);
        }
      }

      // Create result mesh
      const resultMesh = resultCSG.toMesh(new THREE.MeshStandardMaterial({
        color: 0x808080,
        metalness: 0.3,
        roughness: 0.7
      }));
      resultMesh.userData = {
        isCADFeature: true,
        featureId: featureId + '_union',
        featureName: feature.name + '_union'
      };

      // Add to scene
      this.scene.add(resultMesh);
      this.cadObjects.set(featureId + '_union', resultMesh);

      // Hide original meshes
      targetMesh.visible = false;
      previousFeatures.forEach(f => {
        const mesh = this.cadObjects.get(f.id);
        if (mesh) mesh.visible = false;
      });

      // Record in history
      if (this.historyManager) {
        const hiddenIds = [featureId, ...previousFeatures.map(f => f.id)];
        const resultId = featureId + '_union';
        this.historyManager.recordOperation({
          type: 'boolean_union',
          name: 'Boolean Union',
          resultId: resultId,
          hiddenIds: hiddenIds,
          geometry: this.historyManager._serializeGeometry(resultMesh)
        });
      }

      Utils.notify('Boolean union completed', 'success');
    } catch (error) {
      console.error('[CADViewer] Boolean union error:', error);
      Utils.notify('Boolean union failed: ' + error.message, 'error');
    }
  }

  /**
   * Perform boolean cut (subtract) - recomputes geometry
   */
  performBooleanCut(featureId) {
    const feature = this.document?.getFeature(featureId);
    if (!feature) return;

    const targetMesh = this.cadObjects.get(featureId);
    if (!targetMesh) {
      Utils.notify('No geometry to cut', 'warning');
      return;
    }

    // Find previous solid features to subtract from
    const previousFeatures = [];
    for (const f of this.document.features) {
      if (f.id === featureId) break;
      if (f.type === 'extrude' || f.type === 'revolve') {
        previousFeatures.push(f);
      }
    }

    if (previousFeatures.length === 0) {
      Utils.notify('No previous features to cut from', 'info');
      return;
    }

    Utils.notify('Performing boolean subtract...', 'info');

    try {
      // Start with first previous feature
      const baseMesh = this.cadObjects.get(previousFeatures[0].id);
      if (!baseMesh) return;

      let resultCSG = CSG.fromMesh(baseMesh);

      // Subtract this feature
      const cutCSG = CSG.fromMesh(targetMesh);
      resultCSG = resultCSG.subtract(cutCSG);

      // Create result mesh
      const resultMesh = resultCSG.toMesh(new THREE.MeshStandardMaterial({
        color: 0x808080,
        metalness: 0.3,
        roughness: 0.7
      }));
      resultMesh.userData = {
        isCADFeature: true,
        featureId: featureId + '_cut',
        featureName: feature.name + '_cut'
      };

      // Add to scene
      this.scene.add(resultMesh);
      this.cadObjects.set(featureId + '_cut', resultMesh);

      // Hide original meshes
      targetMesh.visible = false;
      baseMesh.visible = false;

      // Record in history
      if (this.historyManager) {
        const resultId = featureId + '_cut';
        this.historyManager.recordOperation({
          type: 'boolean_cut',
          name: 'Boolean Subtract',
          resultId: resultId,
          hiddenIds: [featureId, previousFeatures[0]?.id].filter(Boolean),
          geometry: this.historyManager._serializeGeometry(resultMesh)
        });
      }

      Utils.notify('Boolean subtract completed', 'success');
    } catch (error) {
      console.error('[CADViewer] Boolean subtract error:', error);
      Utils.notify('Boolean subtract failed: ' + error.message, 'error');
    }
  }

  /**
   * Perform boolean intersect - recomputes geometry
   */
  performBooleanIntersect(featureId) {
    const feature = this.document?.getFeature(featureId);
    if (!feature) return;

    const targetMesh = this.cadObjects.get(featureId);
    if (!targetMesh) {
      Utils.notify('No geometry to intersect', 'warning');
      return;
    }

    // Find previous solid features to intersect with
    const previousFeatures = [];
    for (const f of this.document.features) {
      if (f.id === featureId) break;
      if (f.type === 'extrude' || f.type === 'revolve') {
        previousFeatures.push(f);
      }
    }

    if (previousFeatures.length === 0) {
      Utils.notify('No previous features to intersect with', 'info');
      return;
    }

    Utils.notify('Performing boolean intersect...', 'info');

    try {
      let resultCSG = CSG.fromMesh(targetMesh);

      // Intersect with previous features
      for (const prevFeature of previousFeatures) {
        const prevMesh = this.cadObjects.get(prevFeature.id);
        if (prevMesh) {
          const prevCSG = CSG.fromMesh(prevMesh);
          resultCSG = resultCSG.intersect(prevCSG);
        }
      }

      // Create result mesh
      const resultMesh = resultCSG.toMesh(new THREE.MeshStandardMaterial({
        color: 0x808080,
        metalness: 0.3,
        roughness: 0.7
      }));
      resultMesh.userData = {
        isCADFeature: true,
        featureId: featureId + '_intersect',
        featureName: feature.name + '_intersect'
      };

      // Add to scene
      this.scene.add(resultMesh);
      this.cadObjects.set(featureId + '_intersect', resultMesh);

      // Hide original meshes
      targetMesh.visible = false;
      previousFeatures.forEach(f => {
        const mesh = this.cadObjects.get(f.id);
        if (mesh) mesh.visible = false;
      });

      // Record in history
      if (this.historyManager) {
        const hiddenIds = [featureId, ...previousFeatures.map(f => f.id)];
        const resultId = featureId + '_intersect';
        this.historyManager.recordOperation({
          type: 'boolean_intersect',
          name: 'Boolean Intersect',
          resultId: resultId,
          hiddenIds: hiddenIds,
          geometry: this.historyManager._serializeGeometry(resultMesh)
        });
      }

      Utils.notify('Boolean intersect completed', 'success');
    } catch (error) {
      console.error('[CADViewer] Boolean intersect error:', error);
      Utils.notify('Boolean intersect failed: ' + error.message, 'error');
    }
  }

  /**
   * Recompute entire feature tree (apply all boolean operations)
   */
  recomputeFeatureTree() {
    console.log('[CADViewer] Recomputing feature tree');
    // TODO: Implement full feature tree evaluation
    Utils.notify('Feature tree recomputed', 'success');
  }

  /**
   * Edit an existing sketch
   */
  editSketch(featureId) {
    if (!this.sketchEngine) {
      Utils.notify('Sketch engine not available', 'error');
      return;
    }

    this.sketchEngine.editSketch(featureId);
  }

  /**
   * Hide all features that depend on the given feature
   */
  hideDependentFeatures(featureId) {
    const dependentIds = [];

    // Find all features that depend on this one
    this.document.features.forEach(feature => {
      // Check if this feature depends on the given feature
      if (feature.type === 'extrude' && feature.parameters.baseSketch === featureId) {
        dependentIds.push(feature.id);
      }
      // Add more dependency checks for other feature types as needed
    });

    // Hide the meshes for dependent features
    dependentIds.forEach(id => {
      const mesh = this.cadObjects.get(id);
      if (mesh) {
        mesh.visible = false;
        console.log('[CADViewer] Hid dependent feature:', id);
      }
    });

    return dependentIds;
  }

  /**
   * Recompute and show all features that depend on the given feature
   */
  recomputeDependentFeatures(featureId) {
    console.log('[CADViewer] Recomputing dependent features for:', featureId);

    // Find dependent features
    const dependents = this.document.features.filter(feature => {
      if (feature.type === 'extrude' && feature.parameters.baseSketch === featureId) {
        return true;
      }
      // Add more dependency checks as needed
      return false;
    });

    // Recompute each dependent feature
    dependents.forEach(feature => {
      console.log('[CADViewer] Recomputing:', feature.name);

      // Remove old mesh
      const oldMesh = this.cadObjects.get(feature.id);
      if (oldMesh) {
        this.scene.remove(oldMesh);
        if (oldMesh.geometry) oldMesh.geometry.dispose();
        if (oldMesh.material) {
          if (Array.isArray(oldMesh.material)) {
            oldMesh.material.forEach(m => m.dispose());
          } else {
            oldMesh.material.dispose();
          }
        }
        this.cadObjects.delete(feature.id);
      }

      // Recreate based on feature type
      if (feature.type === 'extrude') {
        // Get updated sketch
        const sketch = this.document.getFeature(feature.parameters.baseSketch);
        if (!sketch) return;

        // Convert sketch to shape
        const shape = this.convertSketchToShape(sketch.parameters.entities);
        if (!shape) return;

        // Create extrude geometry
        const extrudeSettings = {
          depth: feature.parameters.distance,
          bevelEnabled: false,
          curveSegments: 32
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        const material = new THREE.MeshStandardMaterial({
          color: 0x808080,
          metalness: 0.3,
          roughness: 0.7
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.userData = {
          isCADFeature: true,
          featureId: feature.id,
          featureName: feature.name
        };

        this.scene.add(mesh);
        this.cadObjects.set(feature.id, mesh);
        mesh.visible = true;

        console.log('[CADViewer] Recomputed extrude:', feature.name);
      }
    });

    Utils.notify(`Recomputed ${dependents.length} dependent feature(s)`, 'success');
  }

  /**
   * Get icon for feature type
   */
  getFeatureIcon(type) {
    const icons = {
      sketch: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="4" width="16" height="16"></rect></svg>`,
      extrude: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="8" height="8"></rect><path d="M12 4 L16 2 L16 10 L12 12 Z" opacity="0.6"></path></svg>`,
      pocket: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="2,2"><rect x="4" y="4" width="8" height="8"></rect></svg>`,
      revolve: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="6"></circle></svg>`,
      groove: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="2,2"><circle cx="12" cy="12" r="6"></circle></svg>`,
      hole: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="8"></circle><circle cx="12" cy="12" r="3"></circle></svg>`,
      sweep: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12 Q12 4, 20 12"></path></svg>`,
      loft: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><ellipse cx="12" cy="6" rx="8" ry="2"></ellipse><ellipse cx="12" cy="18" rx="6" ry="2"></ellipse></svg>`,
      fillet: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4 L4 14 Q4 20, 10 20 L20 20"></path></svg>`,
      chamfer: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4 L4 14 L10 20 L20 20"></path></svg>`,
      shell: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="4" width="16" height="16"></rect><rect x="7" y="7" width="10" height="10"></rect></svg>`,
      linearPattern: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="6" cy="12" r="2"></circle><circle cx="12" cy="12" r="2"></circle><circle cx="18" cy="12" r="2"></circle></svg>`,
      circularPattern: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="4" r="2"></circle><circle cx="20" cy="12" r="2"></circle><circle cx="12" cy="20" r="2"></circle><circle cx="4" cy="12" r="2"></circle></svg>`,
      mirror: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2 L12 22"></path><circle cx="7" cy="12" r="3"></circle><circle cx="17" cy="12" r="3"></circle></svg>`,
      primitive: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="5" width="14" height="14"></rect></svg>`,
      imported: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>`,
      datumPlane: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="8" width="16" height="8" opacity="0.3"></rect></svg>`,
      datumAxis: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="2" x2="12" y2="22"></line></svg>`,
      datumPoint: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="3"></circle></svg>`,
      lcs: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2 L12 12 M12 12 L22 12"></path><circle cx="12" cy="12" r="2"></circle></svg>`,
      surface: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12 Q12 4, 20 12 Q12 20, 4 12"></path></svg>`,
      sheetMetal: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4 L20 4 L20 12 L16 16 L4 16 Z"></path></svg>`,
      gear: `<svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="4"></circle><path d="M12 2 L12 6 M12 18 L12 22 M2 12 L6 12 M18 12 L22 12"></path></svg>`
    };
    return icons[type] || icons.sketch;
  }

  /**
   * Activate a sketch tool
   */
  activateSketchTool(toolName) {
    if (!this.sketchEngine) {
      Utils.notify('Sketch engine not available', 'error');
      return;
    }

    this.sketchEngine.activateTool(toolName);
  }

  /**
   * Show sketch plane selection modal
   */
  showSketchPlaneModal() {
    const modal = document.getElementById('sketchPlaneModal');
    if (!modal) return;

    modal.style.display = 'flex';

    // Wire up plane buttons (one-time setup)
    const planeButtons = modal.querySelectorAll('.sketch-plane-btn');
    planeButtons.forEach(btn => {
      btn.onclick = () => {
        const planeType = btn.dataset.plane;
        modal.style.display = 'none';

        // Create sketch plane
        let plane;
        switch (planeType) {
          case 'xy':
            plane = SketchPlane.XY();
            break;
          case 'xz':
            plane = SketchPlane.XZ();
            break;
          case 'yz':
            plane = SketchPlane.YZ();
            break;
          case 'face':
            // Initialize face selector if needed
            if (!this.faceSelector && typeof FaceSelector !== 'undefined') {
              this.faceSelector = new FaceSelector(this);
            }

            if (this.faceSelector) {
              // Start face selection mode
              this.faceSelector.startSelection((plane) => {
                // Start sketch on selected face
                if (this.sketchEngine) {
                  this.sketchEngine.startSketch(plane);
                }
              });
            } else {
              Utils.notify('Face selection not available', 'error');
            }
            return;
        }

        // Start sketch
        if (this.sketchEngine && plane) {
          this.sketchEngine.startSketch(plane);
        }
      };
    });

    // Close button
    const closeBtn = document.getElementById('btnCloseSketchPlane');
    if (closeBtn) {
      closeBtn.onclick = () => {
        modal.style.display = 'none';
      };
    }
  }

  /**
   * Enter CAD mode (switch from visualization to CAD)
   */
  async enterCADMode() {
    console.log('[CADViewer] Entering CAD mode...');

    // Start CAD mode immediately (don't wait for OpenCASCADE)
    this.currentMode = 'cad';

    // Load OpenCASCADE in background (non-blocking)
    if (!this.kernel) {
      this.kernelLoader.initialize().then(kernel => {
        if (kernel) {
          this.kernel = kernel;
          Utils.notify('OpenCASCADE loaded - Advanced features available!', 'success');
          console.log('[CADViewer] OpenCASCADE ready');
        }
      }).catch(() => {
        console.log('[CADViewer] OpenCASCADE not available - using Three.js (all features work!)');
        this.kernel = null;
      });
    }

    // Request CAD workspace lock if collaboration is active
    if (window.lockManager) {
      const success = window.lockManager.requestLock('cad_workspace', 'CAD Workspace');
      if (!success) {
        Utils.notify('Another user is currently in CAD mode', 'warning');
        // Still allow entering CAD mode as viewer
      }
    }

    // Update presence
    if (window.presenceManager) {
      window.presenceManager.updateStatus('editing CAD');
    }

    // Create new document if none exists
    if (!this.document) {
      await this.createNewDocument();
    }

    console.log('[CADViewer] Entered CAD mode');
    return true;
  }

  /**
   * Exit CAD mode (return to visualization)
   */
  exitCADMode() {
    console.log('[CADViewer] Exiting CAD mode...');

    this.currentMode = 'view';

    // Release CAD workspace lock
    if (window.lockManager) {
      window.lockManager.releaseLock('cad_workspace');
    }

    // Update presence
    if (window.presenceManager) {
      window.presenceManager.updateStatus('viewing');
    }

    // Cancel any active sketch
    if (this.activeSketch) {
      this.cancelSketch();
    }

    console.log('[CADViewer] Exited CAD mode');
  }

  // ===== Document Management =====

  /**
   * Create a new CAD document
   */
  async createNewDocument(name = 'Untitled Document') {
    const token = localStorage.getItem('coandacloud_token');
    if (!token) {
      Utils.notify('Please login to create CAD documents', 'warning');
      return null;
    }

    try {
      const response = await fetch('/api/cad/documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name })
      });

      if (!response.ok) {
        throw new Error('Failed to create document');
      }

      const data = await response.json();
      this.document = new CADDocument(data);

      // Clear history for new document
      if (this.historyManager) {
        this.historyManager.clear();
      }

      Utils.notify(`Created document: ${name}`, 'success');
      return this.document;
    } catch (error) {
      Utils.notify('Failed to create document: ' + error.message, 'error');
      return null;
    }
  }

  /**
   * Load existing CAD document
   */
  async loadDocument(documentId) {
    const token = localStorage.getItem('coandacloud_token');
    if (!token) {
      Utils.notify('Please login to load CAD documents', 'warning');
      return null;
    }

    try {
      const response = await fetch(`/api/cad/documents/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load document');
      }

      const data = await response.json();
      this.document = CADDocument.fromJSON(data);

      // Clear history for loaded document
      if (this.historyManager) {
        this.historyManager.clear();
      }

      // Render all parts
      await this.renderDocument();

      // Update feature tree
      this.updateFeatureTree();

      Utils.notify(`Loaded document: ${this.document.name}`, 'success');
      return this.document;
    } catch (error) {
      Utils.notify('Failed to load document: ' + error.message, 'error');
      return null;
    }
  }

  /**
   * Save current document
   */
  async saveDocument() {
    if (!this.document) {
      Utils.notify('No document to save', 'warning');
      return false;
    }

    const token = localStorage.getItem('coandacloud_token');
    if (!token) {
      Utils.notify('Please login to save documents', 'warning');
      return false;
    }

    try {
      const response = await fetch(`/api/cad/documents/${this.document.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(this.document.toJSON())
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || `HTTP ${response.status}: ${response.statusText}`;
        console.error('[CADViewer] Save failed:', response.status, errorMsg);
        throw new Error(errorMsg);
      }

      this.document.hasUnsavedChanges = false;
      Utils.notify('Document saved', 'success');
      return true;
    } catch (error) {
      console.error('[CADViewer] Save error:', error);
      Utils.notify('Failed to save document: ' + error.message, 'error');
      return false;
    }
  }

  /**
   * Render entire document (all parts and features)
   */
  async renderDocument() {
    if (!this.document) return;

    // Clear existing CAD objects
    this.clearCADObjects();

    // Render all parts (if any)
    for (const part of this.document.parts) {
      await this.renderPart(part);
    }

    // Rebuild geometry from feature tree (for parametric CAD)
    // This ensures features (sketches, extrudes) display correctly after reload
    await this.rebuildFromFeatures();

    // Fit camera to show all geometry
    if (this.cadObjects.size > 0) {
      const firstMesh = Array.from(this.cadObjects.values())[0];
      if (firstMesh && this.engine.fitCameraToObject) {
        this.engine.fitCameraToObject(firstMesh);
      }
    }
  }

  /**
   * Rebuild 3D geometry from feature tree (parametric reconstruction)
   */
  async rebuildFromFeatures() {
    if (!this.document || !this.document.features) return;

    console.log(`[CADViewer] Rebuilding from ${this.document.features.length} features...`);

    for (const feature of this.document.features) {
      try {
        if (feature.type === 'sketch') {
          // Rebuild 3D sketch visualization using SketchEngine
          const sketch = new Sketch(feature.id, feature.parameters.name, feature.parameters.plane);
          sketch.entities = feature.parameters.entities || [];
          if (this.sketchEngine && this.sketchEngine.create3DSketch) {
            this.sketchEngine.create3DSketch(sketch);
          }
        } else if (feature.type === 'extrude') {
          // Rebuild extrude geometry
          await this.rebuildExtrude(feature);
        } else if (feature.type === 'revolve') {
          // Rebuild revolve geometry
          await this.rebuildRevolve(feature);
        } else if (feature.type === 'primitive') {
          // Primitives are stored in parts, already rendered above
        }
        // Add more feature types as needed (fillet, chamfer, pattern, etc.)
      } catch (error) {
        console.error(`[CADViewer] Failed to rebuild feature ${feature.id}:`, error);
      }
    }

    console.log('[CADViewer] Rebuild complete');
  }

  /**
   * Rebuild an extrude feature from saved parameters
   */
  async rebuildExtrude(feature) {
    const baseSketch = this.document.getFeature(feature.parameters.baseSketch);
    if (!baseSketch || baseSketch.type !== 'sketch') {
      console.warn('[CADViewer] Base sketch not found for extrude:', feature.id);
      return;
    }

    // Convert sketch to shape
    const shape = this.convertSketchToShape(baseSketch.parameters.entities);
    if (!shape) return;

    // Create extrude geometry
    const extrudeSettings = {
      depth: feature.parameters.distance,
      bevelEnabled: false,
      curveSegments: 32
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.3,
      roughness: 0.7
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name
    };

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);
  }

  /**
   * Rebuild a revolve feature from saved parameters
   */
  async rebuildRevolve(feature) {
    // TODO: Implement revolve rebuild
    console.warn('[CADViewer] Revolve rebuild not yet implemented');
  }

  // ===== Shape Creation =====

  /**
   * Create a primitive shape
   */
  async createPrimitiveShape(shapeType, parameters) {
    const token = localStorage.getItem('coandacloud_token');

    try {
      // Use server-side geometry service for shape creation
      const response = await fetch('/api/cad/shapes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          shape_type: shapeType,
          parameters: parameters
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create shape');
      }

      const result = await response.json();

      // Create Part from result
      const part = new Part(result.id, result.name || shapeType);
      part.brep = result.brep;
      part.mesh = result.mesh;
      part.properties = result.properties;

      // Add to document
      this.document.parts.push(part);
      this.parts.set(part.id, part);

      // Render in scene
      await this.renderPart(part);

      // Add feature to tree
      const feature = new Feature(
        Utils.generateId(),
        'primitive',
        {
          name: shapeType,
          shapeType: shapeType,
          parameters: parameters,
          partId: part.id
        }
      );
      this.document.addFeature(feature);

      Utils.notify(`Created ${shapeType}`, 'success');
      return part;
    } catch (error) {
      Utils.notify('Failed to create shape: ' + error.message, 'error');
      return null;
    }
  }

  /**
   * Create a Part object from a Three.js mesh
   * @param {THREE.Mesh} mesh - The mesh to convert
   * @param {string} id - Part ID (usually same as feature ID)
   * @param {string} name - Part name
   * @returns {Part} The created Part object
   */
  createPartFromMesh(mesh, id, name) {
    const part = new Part(id, name);

    // Extract mesh data for saving
    const geometry = mesh.geometry;
    if (geometry && geometry.attributes.position) {
      const positions = Array.from(geometry.attributes.position.array);
      const normals = geometry.attributes.normal ? Array.from(geometry.attributes.normal.array) : [];
      const indices = geometry.index ? Array.from(geometry.index.array) : [];

      part.mesh = {
        positions: new Float32Array(positions),
        normals: new Float32Array(normals),
        indices: new Uint32Array(indices)
      };
    }

    // Store material properties
    if (mesh.material) {
      part.appearance = {
        color: mesh.material.color ? mesh.material.color.getHex() : 0x808080,
        metalness: mesh.material.metalness || 0.3,
        roughness: mesh.material.roughness || 0.7,
        opacity: mesh.material.opacity || 1.0
      };
    }

    // Compute bounding box
    geometry.computeBoundingBox();
    if (geometry.boundingBox) {
      part.boundingBox = {
        min: geometry.boundingBox.min.toArray(),
        max: geometry.boundingBox.max.toArray()
      };
    }

    return part;
  }

  /**
   * Render a part in the Three.js scene
   */
  async renderPart(part) {
    // If part already has mesh data, create Three.js mesh
    if (part.mesh) {
      const mesh = part.toThreeMesh();
      if (mesh) {
        this.scene.add(mesh);
        this.cadObjects.set(part.id, mesh);
      }
    } else {
      // Need to tessellate first
      // For now, skip (will be handled by geometry service)
      console.warn('Part has no mesh data, skipping render');
    }
  }

  // ===== Primitive Creation =====

  /**
   * Create parametric primitive shape
   */
  createPrimitive(primitiveType) {
    if (!this.document) {
      Utils.notify('Create a document first', 'warning');
      return;
    }

    let geometry, parameters;

    switch (primitiveType) {
      case 'box':
        const length = parseFloat(prompt('Length:', '1.0'));
        const width = parseFloat(prompt('Width:', '1.0'));
        const height = parseFloat(prompt('Height:', '1.0'));

        if (isNaN(length) || isNaN(width) || isNaN(height)) {
          Utils.notify('Invalid parameters', 'error');
          return;
        }

        geometry = new THREE.BoxGeometry(length, width, height);
        parameters = { length, width, height };
        break;

      case 'sphere':
        const radius = parseFloat(prompt('Radius:', '0.5'));
        const segments = 32;

        if (isNaN(radius)) {
          Utils.notify('Invalid radius', 'error');
          return;
        }

        geometry = new THREE.SphereGeometry(radius, segments, segments);
        parameters = { radius };
        break;

      case 'cylinder':
        const cylRadius = parseFloat(prompt('Radius:', '0.5'));
        const cylHeight = parseFloat(prompt('Height:', '1.0'));

        if (isNaN(cylRadius) || isNaN(cylHeight)) {
          Utils.notify('Invalid parameters', 'error');
          return;
        }

        geometry = new THREE.CylinderGeometry(cylRadius, cylRadius, cylHeight, 32);
        parameters = { radius: cylRadius, height: cylHeight };
        break;

      case 'cone':
        const coneRadius = parseFloat(prompt('Base radius:', '0.5'));
        const coneHeight = parseFloat(prompt('Height:', '1.0'));

        if (isNaN(coneRadius) || isNaN(coneHeight)) {
          Utils.notify('Invalid parameters', 'error');
          return;
        }

        geometry = new THREE.ConeGeometry(coneRadius, coneHeight, 32);
        parameters = { radius: coneRadius, height: coneHeight };
        break;

      case 'torus':
        const torusRadius = parseFloat(prompt('Major radius:', '0.5'));
        const tubeRadius = parseFloat(prompt('Tube radius:', '0.2'));

        if (isNaN(torusRadius) || isNaN(tubeRadius)) {
          Utils.notify('Invalid parameters', 'error');
          return;
        }

        geometry = new THREE.TorusGeometry(torusRadius, tubeRadius, 16, 32);
        parameters = { majorRadius: torusRadius, tubeRadius: tubeRadius };
        break;

      case 'wedge':
        const wedgeLength = parseFloat(prompt('Length:', '1.0'));
        const wedgeWidth = parseFloat(prompt('Width:', '1.0'));
        const wedgeHeight = parseFloat(prompt('Height:', '1.0'));

        if (isNaN(wedgeLength) || isNaN(wedgeWidth) || isNaN(wedgeHeight)) {
          Utils.notify('Invalid parameters', 'error');
          return;
        }

        // Create wedge using custom geometry
        geometry = this.createWedgeGeometry(wedgeLength, wedgeWidth, wedgeHeight);
        parameters = { length: wedgeLength, width: wedgeWidth, height: wedgeHeight };
        break;

      default:
        Utils.notify('Unknown primitive type', 'error');
        return;
    }

    // Create feature
    const feature = new Feature(
      Utils.generateId(),
      'primitive',
      {
        name: `${primitiveType.charAt(0).toUpperCase() + primitiveType.slice(1)}${this.document.features.length + 1}`,
        primitiveType: primitiveType,
        ...parameters
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Create mesh
    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.3,
      roughness: 0.7
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name
    };

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'create_primitive',
        name: `Create ${primitiveType}`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: this.historyManager._serializeGeometry(mesh)
      });
    }

    Utils.notify(`Created ${primitiveType}`, 'success');
  }

  /**
   * Create custom wedge geometry
   */
  createWedgeGeometry(length, width, height) {
    const shape = new THREE.Shape();

    // Create triangular profile
    shape.moveTo(0, 0);
    shape.lineTo(width, 0);
    shape.lineTo(width, height);
    shape.lineTo(0, 0);

    // Extrude to create wedge
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: length,
      bevelEnabled: false
    });

    return geometry;
  }

  // ===== Utility Methods =====

  /**
   * Clear all CAD objects from scene
   */
  clearCADObjects() {
    this.cadObjects.forEach((mesh, id) => {
      this.scene.remove(mesh);
      // Dispose geometry and material to free memory
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(m => m.dispose());
        } else {
          mesh.material.dispose();
        }
      }
    });
    this.cadObjects.clear();
  }

  /**
   * Get feature by ID
   */
  getFeature(featureId) {
    return this.document?.features.find(f => f.id === featureId);
  }

  /**
   * Get part by ID
   */
  getPart(partId) {
    return this.parts.get(partId);
  }

  /**
   * Toggle part visibility
   */
  togglePartVisibility(partId) {
    const mesh = this.cadObjects.get(partId);
    if (mesh) {
      mesh.visible = !mesh.visible;

      const part = this.getPart(partId);
      if (part) {
        part.visible = mesh.visible;
      }
    }
  }

  /**
   * Cancel active sketch
   */
  cancelSketch() {
    if (!this.activeSketch) return;

    // Release sketch lock
    if (window.lockManager) {
      window.lockManager.releaseLock(`sketch:${this.activeSketch.id}`);
    }

    this.activeSketch = null;
    this.activeTool = null;

    Utils.notify('Sketch cancelled', 'info');
  }

  /**
   * Check if viewer is ready for CAD operations
   */
  isReady() {
    return this.kernelLoader.isReady();
  }

  /**
   * Perform extrude operation on selected sketch
   */
  performExtrude() {
    console.log('[CADViewer] Performing extrude');

    // Check if a sketch is selected
    const selectedFeature = this.document?.getFeature(this.selectedFeatureId);

    if (!selectedFeature || selectedFeature.type !== 'sketch') {
      Utils.notify('Please select a sketch to extrude', 'warning');
      return;
    }

    // Prompt for distance
    const distance = prompt('Enter extrude distance:', '1.0');
    if (!distance) return;

    const dist = parseFloat(distance);
    if (isNaN(dist)) {
      Utils.notify('Invalid distance', 'error');
      return;
    }

    // Create extrude feature
    const feature = new Feature(
      Utils.generateId(),
      'extrude',
      {
        name: `Extrude${this.document.features.length + 1}`,
        baseSketch: selectedFeature.id,
        distance: dist,
        direction: 'normal'
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Convert sketch to Three.js Shape
    const shape = this.convertSketchToShape(selectedFeature.parameters.entities);

    if (!shape) {
      Utils.notify('Could not convert sketch to shape', 'error');
      return;
    }

    // Create extrude geometry with actual sketch profile
    const extrudeSettings = {
      depth: dist,
      bevelEnabled: false,
      curveSegments: 32
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.3,
      roughness: 0.7
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name
    };

    // Create Part from mesh and add to document.parts
    const part = this.createPartFromMesh(mesh, feature.id, feature.name);
    this.document.parts.push(part);
    this.parts.set(part.id, part);
    this.document.hasUnsavedChanges = true;

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    // Record in history (include part data for undo/redo)
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Extrude ${selectedFeature.name}`,
        featureId: feature.id,
        partId: part.id,
        feature: JSON.parse(JSON.stringify(feature)),
        part: JSON.parse(JSON.stringify(part)),
        geometry: this.historyManager._serializeGeometry(mesh)
      });
    }

    Utils.notify(`Extruded ${selectedFeature.name} (distance: ${dist})`, 'success');
  }

  /**
   * Convert sketch entities to Three.js Shape
   */
  convertSketchToShape(entities) {
    if (!entities || entities.length === 0) return null;

    // Filter out construction entities - they are only for reference
    const regularEntities = entities.filter(entity => !entity.construction);

    if (regularEntities.length === 0) {
      console.log('[CADViewer] No regular entities found (all are construction geometry)');
      return null;
    }

    const shape = new THREE.Shape();

    // Find the first line or point to start
    let started = false;

    regularEntities.forEach((entity, index) => {
      switch (entity.type) {
        case 'line':
          if (!started) {
            shape.moveTo(entity.start.x, entity.start.y);
            started = true;
          }
          shape.lineTo(entity.end.x, entity.end.y);
          break;

        case 'rectangle':
          // Rectangle as 4 lines
          shape.moveTo(entity.start.x, entity.start.y);
          shape.lineTo(entity.end.x, entity.start.y);
          shape.lineTo(entity.end.x, entity.end.y);
          shape.lineTo(entity.start.x, entity.end.y);
          shape.lineTo(entity.start.x, entity.start.y);
          started = true;
          break;

        case 'circle':
          // Circle as arc
          shape.absarc(entity.center.x, entity.center.y, entity.radius, 0, Math.PI * 2, false);
          started = true;
          break;

        case 'arc':
          // Arc segment
          if (!started) {
            shape.moveTo(entity.start.x, entity.start.y);
            started = true;
          }
          shape.absarc(entity.center.x, entity.center.y, entity.radius, entity.startAngle, entity.endAngle, false);
          break;

        case 'ellipse':
          // Ellipse
          shape.absellipse(
            entity.center.x,
            entity.center.y,
            entity.radiusX,
            entity.radiusY,
            0, Math.PI * 2, false, 0
          );
          started = true;
          break;

        case 'polygon':
          // Polygon as connected lines
          for (let i = 0; i <= entity.sides; i++) {
            const angle = (i * 2 * Math.PI) / entity.sides - Math.PI / 2;
            const x = entity.center.x + entity.radius * Math.cos(angle);
            const y = entity.center.y + entity.radius * Math.sin(angle);

            if (i === 0) {
              shape.moveTo(x, y);
              started = true;
            } else {
              shape.lineTo(x, y);
            }
          }
          break;

        case 'slot':
          // Slot as rounded rectangle
          const slotRadius = entity.width / 2;

          // Move to start
          shape.moveTo(entity.start.x + slotRadius, entity.start.y);
          // Top line
          shape.lineTo(entity.end.x - slotRadius, entity.start.y);
          // Right semicircle
          shape.absarc(
            entity.end.x - slotRadius,
            entity.start.y + slotRadius,
            slotRadius,
            -Math.PI / 2,
            Math.PI / 2,
            false
          );
          // Bottom line
          shape.lineTo(entity.start.x + slotRadius, entity.end.y);
          // Left semicircle
          shape.absarc(
            entity.start.x + slotRadius,
            entity.start.y + slotRadius,
            slotRadius,
            Math.PI / 2,
            Math.PI * 1.5,
            false
          );
          started = true;
          break;
      }
    });

    return shape;
  }

  /**
   * Perform revolve operation
   */
  performRevolve() {
    console.log('[CADViewer] Performing revolve');

    const selectedFeature = this.document?.getFeature(this.selectedFeatureId);

    if (!selectedFeature || selectedFeature.type !== 'sketch') {
      Utils.notify('Please select a sketch to revolve', 'warning');
      return;
    }

    // Prompt for angle
    const angle = prompt('Enter revolve angle (degrees, 0-360):', '360');
    if (!angle) return;

    const angleRad = (parseFloat(angle) * Math.PI) / 180;
    if (isNaN(angleRad)) {
      Utils.notify('Invalid angle', 'error');
      return;
    }

    // Create revolve feature
    const feature = new Feature(
      Utils.generateId(),
      'revolve',
      {
        name: `Revolve${this.document.features.length + 1}`,
        baseSketch: selectedFeature.id,
        angle: angleRad,
        axis: 'Y' // Default to Y-axis
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Convert sketch to shape
    const shape = this.convertSketchToShape(selectedFeature.parameters.entities);
    if (!shape) {
      Utils.notify('Could not convert sketch to shape', 'error');
      return;
    }

    // Create revolve geometry using LatheGeometry
    const points = [];
    shape.getPoints(32).forEach(pt => {
      points.push(new THREE.Vector2(Math.abs(pt.x), pt.y)); // Use abs for proper revolution
    });

    const geometry = new THREE.LatheGeometry(points, 32, 0, angleRad);
    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.3,
      roughness: 0.7
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name
    };

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Revolve ${selectedFeature.name}`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: this.historyManager._serializeGeometry(mesh)
      });
    }

    Utils.notify(`Revolved ${selectedFeature.name} (angle: ${angle}°)`, 'success');
  }

  /**
   * Perform pocket operation (extrude cut)
   */
  performPocket() {
    console.log('[CADViewer] Performing pocket');

    const selectedFeature = this.document?.getFeature(this.selectedFeatureId);

    if (!selectedFeature || selectedFeature.type !== 'sketch') {
      Utils.notify('Please select a sketch to pocket', 'warning');
      return;
    }

    // Prompt for depth
    const depth = prompt('Enter pocket depth:', '0.5');
    if (!depth) return;

    const d = parseFloat(depth);
    if (isNaN(d)) {
      Utils.notify('Invalid depth', 'error');
      return;
    }

    // Create pocket feature (extrude cut)
    const feature = new Feature(
      Utils.generateId(),
      'pocket',
      {
        name: `Pocket${this.document.features.length + 1}`,
        baseSketch: selectedFeature.id,
        depth: d,
        direction: 'normal'
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Convert sketch to shape
    const shape = this.convertSketchToShape(selectedFeature.parameters.entities);
    if (!shape) {
      Utils.notify('Could not convert sketch to shape', 'error');
      return;
    }

    // Create extrude geometry (will be subtracted)
    const extrudeSettings = {
      depth: d,
      bevelEnabled: false,
      curveSegments: 32
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const material = new THREE.MeshStandardMaterial({
      color: 0xFF6600, // Orange to indicate cut operation
      metalness: 0.3,
      roughness: 0.7,
      transparent: true,
      opacity: 0.6
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name,
      isSubtractive: true
    };

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Pocket (depth: ${d})`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: this.historyManager._serializeGeometry(mesh)
      });
    }

    Utils.notify(`Pocket created (depth: ${d}) - Click Cut to apply`, 'success');
  }

  /**
   * Perform groove operation (revolve cut)
   */
  performGroove() {
    console.log('[CADViewer] Performing groove');

    const selectedFeature = this.document?.getFeature(this.selectedFeatureId);

    if (!selectedFeature || selectedFeature.type !== 'sketch') {
      Utils.notify('Please select a sketch to groove', 'warning');
      return;
    }

    // Prompt for angle
    const angle = prompt('Enter groove angle (degrees, 0-360):', '360');
    if (!angle) return;

    const angleRad = (parseFloat(angle) * Math.PI) / 180;
    if (isNaN(angleRad)) {
      Utils.notify('Invalid angle', 'error');
      return;
    }

    // Create groove feature (revolve cut)
    const feature = new Feature(
      Utils.generateId(),
      'groove',
      {
        name: `Groove${this.document.features.length + 1}`,
        baseSketch: selectedFeature.id,
        angle: angleRad,
        axis: 'Y'
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Convert sketch to shape and create lathe geometry
    const shape = this.convertSketchToShape(selectedFeature.parameters.entities);
    if (!shape) {
      Utils.notify('Could not convert sketch to shape', 'error');
      return;
    }

    const points = [];
    shape.getPoints(32).forEach(pt => {
      points.push(new THREE.Vector2(Math.abs(pt.x), pt.y));
    });

    const geometry = new THREE.LatheGeometry(points, 32, 0, angleRad);
    const material = new THREE.MeshStandardMaterial({
      color: 0xFF6600, // Orange to indicate cut
      metalness: 0.3,
      roughness: 0.7,
      transparent: true,
      opacity: 0.6
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name,
      isSubtractive: true
    };

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Groove (angle: ${angle}°)`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: this.historyManager._serializeGeometry(mesh)
      });
    }

    Utils.notify(`Groove created (angle: ${angle}°) - Click Cut to apply`, 'success');
  }

  /**
   * Perform hole operation (parametric hole)
   */
  performHole() {
    console.log('[CADViewer] Performing hole');

    // Prompt for hole parameters
    const diameter = parseFloat(prompt('Hole diameter:', '0.2'));
    const depth = parseFloat(prompt('Hole depth (0 for through):', '0.5'));

    if (isNaN(diameter) || diameter <= 0) {
      Utils.notify('Invalid diameter', 'error');
      return;
    }

    const holeType = prompt('Hole type (simple/countersink/counterbore):', 'simple');

    // Create hole feature
    const feature = new Feature(
      Utils.generateId(),
      'hole',
      {
        name: `Hole${this.document.features.length + 1}`,
        diameter: diameter,
        depth: depth || 999, // Large number for through hole
        holeType: holeType,
        position: { x: 0, y: 0, z: 0 } // TODO: Get from click position
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Create hole geometry based on type
    let geometry;
    const radius = diameter / 2;

    switch (holeType) {
      case 'countersink':
        // Create countersink (cylinder + cone)
        const countersinkAngle = 90 * Math.PI / 180;
        const countersinkDepth = radius / Math.tan(countersinkAngle / 2);

        // Simplified: just create cylinder for now
        geometry = new THREE.CylinderGeometry(radius, radius, depth || 1, 32);
        break;

      case 'counterbore':
        // Create counterbore (cylinder + larger cylinder)
        geometry = new THREE.CylinderGeometry(radius, radius, depth || 1, 32);
        break;

      case 'simple':
      default:
        // Simple cylindrical hole
        geometry = new THREE.CylinderGeometry(radius, radius, depth || 1, 32);
        break;
    }

    const material = new THREE.MeshStandardMaterial({
      color: 0xFF6600,
      metalness: 0.3,
      roughness: 0.7,
      transparent: true,
      opacity: 0.6
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.x = Math.PI / 2; // Rotate to point along Z
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name,
      isSubtractive: true
    };

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Hole (${holeType}, ø${diameter})`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: this.historyManager._serializeGeometry(mesh)
      });
    }

    Utils.notify(`Hole created (${holeType}, ø${diameter}) - Click Cut to apply`, 'success');
  }

  /**
   * Perform sweep operation (extrude along path)
   */
  performSweep() {
    console.log('[CADViewer] Performing sweep');

    const selectedFeature = this.document?.getFeature(this.selectedFeatureId);

    if (!selectedFeature || selectedFeature.type !== 'sketch') {
      Utils.notify('Please select a profile sketch to sweep', 'warning');
      return;
    }

    // For now, create a simple curved path
    // TODO: Allow user to select a path sketch
    Utils.notify('Sweep along default curved path', 'info');

    const feature = new Feature(
      Utils.generateId(),
      'sweep',
      {
        name: `Sweep${this.document.features.length + 1}`,
        profileSketch: selectedFeature.id,
        pathCurve: 'default'
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Convert sketch to shape
    const shape = this.convertSketchToShape(selectedFeature.parameters.entities);
    if (!shape) {
      Utils.notify('Could not convert sketch to shape', 'error');
      return;
    }

    // Create a curved path (quadratic bezier)
    const path = new THREE.QuadraticBezierCurve3(
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(2, 2, 0),
      new THREE.Vector3(4, 0, 0)
    );

    const geometry = new THREE.ExtrudeGeometry(shape, {
      extrudePath: path,
      steps: 50,
      bevelEnabled: false
    });

    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.3,
      roughness: 0.7
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name
    };

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Sweep ${selectedFeature.name}`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: this.historyManager._serializeGeometry(mesh)
      });
    }

    Utils.notify(`Swept ${selectedFeature.name}`, 'success');
  }

  /**
   * Perform loft operation (blend between profiles)
   */
  performLoft() {
    console.log('[CADViewer] Performing loft');

    // Get all selected sketches (need at least 2)
    const sketches = this.document?.features.filter(f =>
      f.type === 'sketch' && f.visible
    );

    if (!sketches || sketches.length < 2) {
      Utils.notify('Please create at least 2 sketches for loft', 'warning');
      return;
    }

    // For now, loft between first 2 sketches
    Utils.notify(`Lofting between ${sketches[0].name} and ${sketches[1].name}`, 'info');

    const feature = new Feature(
      Utils.generateId(),
      'loft',
      {
        name: `Loft${this.document.features.length + 1}`,
        profiles: [sketches[0].id, sketches[1].id]
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Create loft geometry (simplified - use cylinder between profiles)
    const geometry = new THREE.CylinderGeometry(0.5, 1.0, 2, 32);
    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.3,
      roughness: 0.7
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name
    };

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: 'Loft',
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: this.historyManager._serializeGeometry(mesh)
      });
    }

    Utils.notify('Loft created', 'success');
  }

  /**
   * Perform fillet operation (round edges)
   */
  performFillet() {
    console.log('[CADViewer] Performing fillet');

    if (!this.selectedFeatureId) {
      Utils.notify('Please select a feature to fillet', 'warning');
      return;
    }

    const radius = prompt('Enter fillet radius:', '0.1');
    if (!radius) return;

    const r = parseFloat(radius);
    if (isNaN(r) || r <= 0) {
      Utils.notify('Invalid radius', 'error');
      return;
    }

    const feature = new Feature(
      Utils.generateId(),
      'fillet',
      {
        name: `Fillet${this.document.features.length + 1}`,
        targetFeature: this.selectedFeatureId,
        radius: r
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Fillet (radius: ${r})`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: null
      });
    }

    Utils.notify(`Fillet created (radius: ${r}) - Visual update requires recompute`, 'info');
  }

  /**
   * Perform chamfer operation (bevel edges)
   */
  performChamfer() {
    console.log('[CADViewer] Performing chamfer');

    if (!this.selectedFeatureId) {
      Utils.notify('Please select a feature to chamfer', 'warning');
      return;
    }

    const distance = prompt('Enter chamfer distance:', '0.1');
    if (!distance) return;

    const d = parseFloat(distance);
    if (isNaN(d) || d <= 0) {
      Utils.notify('Invalid distance', 'error');
      return;
    }

    const feature = new Feature(
      Utils.generateId(),
      'chamfer',
      {
        name: `Chamfer${this.document.features.length + 1}`,
        targetFeature: this.selectedFeatureId,
        distance: d
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Chamfer (distance: ${d})`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: null
      });
    }

    Utils.notify(`Chamfer created (distance: ${d}) - Visual update requires recompute`, 'info');
  }

  /**
   * Perform shell operation (hollow out)
   */
  performShell() {
    console.log('[CADViewer] Performing shell');

    if (!this.selectedFeatureId) {
      Utils.notify('Please select a solid to shell', 'warning');
      return;
    }

    const thickness = prompt('Enter shell thickness:', '0.1');
    if (!thickness) return;

    const t = parseFloat(thickness);
    if (isNaN(t) || t <= 0) {
      Utils.notify('Invalid thickness', 'error');
      return;
    }

    const feature = new Feature(
      Utils.generateId(),
      'shell',
      {
        name: `Shell${this.document.features.length + 1}`,
        targetFeature: this.selectedFeatureId,
        thickness: t
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Shell (thickness: ${t})`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: null
      });
    }

    Utils.notify(`Shell created (thickness: ${t}) - Visual update requires recompute`, 'info');
  }

  /**
   * Perform linear pattern operation
   */
  performLinearPattern() {
    console.log('[CADViewer] Performing linear pattern');

    if (!this.selectedFeatureId) {
      Utils.notify('Please select a feature to pattern', 'warning');
      return;
    }

    const count = prompt('Enter number of instances:', '3');
    const spacing = prompt('Enter spacing:', '1.0');

    if (!count || !spacing) return;

    const n = parseInt(count);
    const s = parseFloat(spacing);

    if (isNaN(n) || n < 2 || isNaN(s) || s <= 0) {
      Utils.notify('Invalid parameters', 'error');
      return;
    }

    const feature = new Feature(
      Utils.generateId(),
      'linearPattern',
      {
        name: `LinearPattern${this.document.features.length + 1}`,
        targetFeature: this.selectedFeatureId,
        count: n,
        spacing: s,
        direction: [1, 0, 0] // X-axis by default
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Create pattern visually
    const sourceMesh = this.cadObjects.get(this.selectedFeatureId);
    const patternInstanceIds = [feature.id]; // Track all instance IDs for undo
    if (sourceMesh) {
      for (let i = 1; i < n; i++) {
        const clonedMesh = sourceMesh.clone();
        clonedMesh.position.x = sourceMesh.position.x + (i * s);
        const instanceId = feature.id + '_' + i;
        clonedMesh.userData = {
          isCADFeature: true,
          featureId: instanceId,
          featureName: feature.name + '_' + i
        };
        this.scene.add(clonedMesh);
        this.cadObjects.set(instanceId, clonedMesh); // Track in cadObjects for undo
        patternInstanceIds.push(instanceId);
      }
    }

    // Record in history - use custom undo/redo via 'linear_pattern' type
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'linear_pattern',
        name: `Linear Pattern (${n} instances)`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        patternInstanceIds: patternInstanceIds
      });
    }

    Utils.notify(`Linear pattern created (${n} instances)`, 'success');
  }

  /**
   * Perform circular pattern operation
   */
  performCircularPattern() {
    console.log('[CADViewer] Performing circular pattern');

    if (!this.selectedFeatureId) {
      Utils.notify('Please select a feature to pattern', 'warning');
      return;
    }

    const count = prompt('Enter number of instances:', '6');
    if (!count) return;

    const n = parseInt(count);
    if (isNaN(n) || n < 2) {
      Utils.notify('Invalid count', 'error');
      return;
    }

    const feature = new Feature(
      Utils.generateId(),
      'circularPattern',
      {
        name: `CircularPattern${this.document.features.length + 1}`,
        targetFeature: this.selectedFeatureId,
        count: n,
        axis: [0, 1, 0] // Y-axis by default
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Create pattern visually
    const sourceMesh = this.cadObjects.get(this.selectedFeatureId);
    const circularInstanceIds = [feature.id]; // Track all instance IDs for undo
    if (sourceMesh) {
      const angleStep = (2 * Math.PI) / n;
      const radius = 2; // Default radius

      for (let i = 1; i < n; i++) {
        const clonedMesh = sourceMesh.clone();
        const angle = i * angleStep;
        clonedMesh.position.x = Math.cos(angle) * radius;
        clonedMesh.position.z = Math.sin(angle) * radius;
        const instanceId = feature.id + '_' + i;
        clonedMesh.userData = {
          isCADFeature: true,
          featureId: instanceId,
          featureName: feature.name + '_' + i
        };
        this.scene.add(clonedMesh);
        this.cadObjects.set(instanceId, clonedMesh); // Track in cadObjects for undo
        circularInstanceIds.push(instanceId);
      }
    }

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'circular_pattern',
        name: `Circular Pattern (${n} instances)`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        patternInstanceIds: circularInstanceIds
      });
    }

    Utils.notify(`Circular pattern created (${n} instances)`, 'success');
  }

  /**
   * Perform mirror operation
   */
  performMirror() {
    console.log('[CADViewer] Performing mirror');

    if (!this.selectedFeatureId) {
      Utils.notify('Please select a feature to mirror', 'warning');
      return;
    }

    const plane = prompt('Mirror plane (XY, XZ, or YZ):', 'XY');
    if (!plane) return;

    const planeUpper = plane.toUpperCase();
    if (!['XY', 'XZ', 'YZ'].includes(planeUpper)) {
      Utils.notify('Invalid plane. Use XY, XZ, or YZ', 'error');
      return;
    }

    const feature = new Feature(
      Utils.generateId(),
      'mirror',
      {
        name: `Mirror${this.document.features.length + 1}`,
        targetFeature: this.selectedFeatureId,
        plane: planeUpper
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Create mirrored geometry
    const sourceMesh = this.cadObjects.get(this.selectedFeatureId);
    if (sourceMesh) {
      const mirroredMesh = sourceMesh.clone();

      // Apply mirror transformation based on plane
      switch (planeUpper) {
        case 'XY':
          mirroredMesh.scale.z = -1;
          break;
        case 'XZ':
          mirroredMesh.scale.y = -1;
          break;
        case 'YZ':
          mirroredMesh.scale.x = -1;
          break;
      }

      mirroredMesh.userData = {
        isCADFeature: true,
        featureId: feature.id,
        featureName: feature.name
      };
      this.scene.add(mirroredMesh);
      this.cadObjects.set(feature.id, mirroredMesh);

      // Record in history
      if (this.historyManager) {
        this.historyManager.recordOperation({
          type: 'add_feature',
          name: `Mirror across ${planeUpper}`,
          featureId: feature.id,
          feature: JSON.parse(JSON.stringify(feature)),
          geometry: this.historyManager._serializeGeometry(mirroredMesh)
        });
      }
    } else {
      // No mesh to mirror - still record feature-only operation
      if (this.historyManager) {
        this.historyManager.recordOperation({
          type: 'add_feature',
          name: `Mirror across ${planeUpper}`,
          featureId: feature.id,
          feature: JSON.parse(JSON.stringify(feature)),
          geometry: null
        });
      }
    }

    Utils.notify(`Mirrored across ${planeUpper} plane`, 'success');
  }

  /**
   * Get CAD viewer status
   */
  getStatus() {
    return {
      mode: this.currentMode,
      kernelLoaded: this.kernelLoader.isReady(),
      hasDocument: this.document !== null,
      activeSketch: this.activeSketch !== null,
      partCount: this.parts.size,
      featureCount: this.document?.features.length || 0,
      assemblyCount: this.document?.assemblies.length || 0
    };
  }

  // ===== Assembly System =====

  /**
   * Create new assembly
   */
  createAssembly(name = 'Assembly') {
    if (!this.document) {
      Utils.notify('Create a document first', 'warning');
      return null;
    }

    const assembly = new Assembly(Utils.generateId(), name);
    this.document.assemblies.push(assembly);

    Utils.notify(`Created assembly: ${name}`, 'success');
    return assembly;
  }

  /**
   * Add part to assembly
   */
  addPartToAssembly(assemblyId, partId, transform = null) {
    const assembly = this.document.assemblies.find(a => a.id === assemblyId);
    if (!assembly) {
      Utils.notify('Assembly not found', 'error');
      return null;
    }

    const component = assembly.addComponent(partId, transform);

    // Create visual representation
    const part = this.parts.get(partId);
    if (part) {
      const mesh = part.toThreeMesh();
      if (mesh && transform) {
        mesh.position.set(...transform.position);
        // Apply quaternion rotation if needed
      }
      this.scene.add(mesh);
    }

    Utils.notify(`Added part to ${assembly.name}`, 'success');
    return component;
  }

  /**
   * Create mate between components
   */
  createMate(assemblyId, component1Id, component2Id, mateType, parameters = {}) {
    const assembly = this.document.assemblies.find(a => a.id === assemblyId);
    if (!assembly) {
      Utils.notify('Assembly not found', 'error');
      return null;
    }

    const mate = new Mate(mateType, component1Id, component2Id, parameters);
    assembly.addMate(mate);

    // Apply mate immediately
    assembly.solveMates();

    // Update visual representation
    this.updateAssemblyVisuals(assembly);

    Utils.notify(`Created ${mateType} mate`, 'success');
    return mate;
  }

  /**
   * Update assembly visuals after mate changes
   */
  updateAssemblyVisuals(assembly) {
    assembly.components.forEach(component => {
      const mesh = this.cadObjects.get(component.partId);
      if (mesh) {
        // Update mesh transform based on component transform
        mesh.position.set(...component.transform.position);

        // Apply quaternion rotation
        const quat = new THREE.Quaternion(...component.transform.rotation);
        mesh.setRotationFromQuaternion(quat);
      }
    });
  }

  /**
   * Simulate assembly motion
   */
  simulateAssemblyMotion(assemblyId) {
    const assembly = this.document.assemblies.find(a => a.id === assemblyId);
    if (!assembly) {
      Utils.notify('Assembly not found', 'error');
      return;
    }

    assembly.isSimulating = !assembly.isSimulating;

    if (assembly.isSimulating) {
      Utils.notify('Started assembly simulation', 'info');
      this.runAssemblySimulation(assembly);
    } else {
      Utils.notify('Stopped assembly simulation', 'info');
    }
  }

  /**
   * Run assembly simulation loop
   */
  runAssemblySimulation(assembly) {
    if (!assembly.isSimulating) return;

    // Find revolute mates and animate them
    assembly.mates.forEach(mate => {
      if (mate.type === 'revolute' && mate.enabled && !mate.suppressed) {
        // Increment angle
        mate.angle = (mate.angle || 0) + 0.01;
        if (mate.angle > Math.PI * 2) mate.angle = 0;
      } else if (mate.type === 'slider' && mate.enabled && !mate.suppressed) {
        // Oscillate distance
        mate.distance = Math.sin(Date.now() * 0.001) * 2;
      }
    });

    // Apply all mates
    assembly.solveMates();

    // Update visuals
    this.updateAssemblyVisuals(assembly);

    // Continue animation
    if (assembly.isSimulating) {
      requestAnimationFrame(() => this.runAssemblySimulation(assembly));
    }
  }

  /**
   * Fix component in assembly (make immovable)
   */
  fixComponent(assemblyId, componentId) {
    const assembly = this.document.assemblies.find(a => a.id === assemblyId);
    if (!assembly) return;

    const component = assembly.getComponent(componentId);
    if (component) {
      component.fixed = !component.fixed;
      Utils.notify(`Component ${component.fixed ? 'fixed' : 'unfixed'}`, 'info');
    }
  }

  // ===== Datum & Reference Geometry =====

  /**
   * Create datum plane
   */
  createDatumPlane() {
    const offset = parseFloat(prompt('Offset from XY plane:', '0'));
    if (isNaN(offset)) return;

    const feature = new Feature(
      Utils.generateId(),
      'datumPlane',
      {
        name: `DatumPlane${this.document.features.length + 1}`,
        basePlane: 'XY',
        offset: offset
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Create visual representation
    const size = 5;
    const geometry = new THREE.PlaneGeometry(size, size);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00AAFF,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.z = offset;
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name,
      isDatum: true
    };

    // Add grid helper
    const gridHelper = new THREE.GridHelper(size, 10, 0x00AAFF, 0x00AAFF);
    gridHelper.position.z = offset;
    gridHelper.rotation.x = Math.PI / 2;
    mesh.add(gridHelper);

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Datum Plane (Z=${offset})`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: null // Datum planes use non-standard geometry
      });
    }

    Utils.notify(`Datum plane created at Z=${offset}`, 'success');
  }

  /**
   * Create datum axis
   */
  createDatumAxis() {
    const axis = prompt('Axis direction (X, Y, or Z):', 'Z');
    if (!axis) return;

    const axisUpper = axis.toUpperCase();
    if (!['X', 'Y', 'Z'].includes(axisUpper)) {
      Utils.notify('Invalid axis', 'error');
      return;
    }

    const feature = new Feature(
      Utils.generateId(),
      'datumAxis',
      {
        name: `DatumAxis${this.document.features.length + 1}`,
        direction: axisUpper
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Create visual axis line
    const points = [];
    const length = 10;

    switch (axisUpper) {
      case 'X':
        points.push(new THREE.Vector3(-length/2, 0, 0));
        points.push(new THREE.Vector3(length/2, 0, 0));
        break;
      case 'Y':
        points.push(new THREE.Vector3(0, -length/2, 0));
        points.push(new THREE.Vector3(0, length/2, 0));
        break;
      case 'Z':
        points.push(new THREE.Vector3(0, 0, -length/2));
        points.push(new THREE.Vector3(0, 0, length/2));
        break;
    }

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color: 0xFF00FF,
      linewidth: 2
    });
    const line = new THREE.Line(geometry, material);
    line.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name,
      isDatum: true
    };

    this.scene.add(line);
    this.cadObjects.set(feature.id, line);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Datum Axis (${axisUpper})`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: null // Datum axes use line geometry, handled specially
      });
    }

    Utils.notify(`Datum axis created (${axisUpper})`, 'success');
  }

  /**
   * Create datum point
   */
  createDatumPoint() {
    const x = parseFloat(prompt('X coordinate:', '0'));
    const y = parseFloat(prompt('Y coordinate:', '0'));
    const z = parseFloat(prompt('Z coordinate:', '0'));

    if (isNaN(x) || isNaN(y) || isNaN(z)) {
      Utils.notify('Invalid coordinates', 'error');
      return;
    }

    const feature = new Feature(
      Utils.generateId(),
      'datumPoint',
      {
        name: `DatumPoint${this.document.features.length + 1}`,
        position: { x, y, z }
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Create visual point
    const geometry = new THREE.SphereGeometry(0.05, 16, 16);
    const material = new THREE.MeshBasicMaterial({ color: 0xFF00FF });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, y, z);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name,
      isDatum: true
    };

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: `Datum Point (${x}, ${y}, ${z})`,
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: this.historyManager._serializeGeometry(mesh)
      });
    }

    Utils.notify(`Datum point created at (${x}, ${y}, ${z})`, 'success');
  }

  /**
   * Create local coordinate system
   */
  createLocalCoordinateSystem() {
    const feature = new Feature(
      Utils.generateId(),
      'lcs',
      {
        name: `LCS${this.document.features.length + 1}`,
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 }
      }
    );

    this.document.addFeature(feature);
    this.updateFeatureTree();

    // Create axes helper
    const axesHelper = new THREE.AxesHelper(1);
    axesHelper.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name,
      isDatum: true
    };

    this.scene.add(axesHelper);
    this.cadObjects.set(feature.id, axesHelper);

    // Record in history
    if (this.historyManager) {
      this.historyManager.recordOperation({
        type: 'add_feature',
        name: 'Local Coordinate System',
        featureId: feature.id,
        feature: JSON.parse(JSON.stringify(feature)),
        geometry: null // AxesHelper is not serializable
      });
    }

    Utils.notify('Local coordinate system created', 'success');
  }

  // ===== Measurement Tools =====

  /**
   * Measure distance between two points/features
   */
  measureDistance() {
    Utils.notify('Click two points to measure distance', 'info');

    // Simplified: measure between last two features
    const features = this.document?.features.slice(-2) || [];
    if (features.length < 2) {
      Utils.notify('Need at least 2 features', 'warning');
      return;
    }

    const mesh1 = this.cadObjects.get(features[0].id);
    const mesh2 = this.cadObjects.get(features[1].id);

    if (!mesh1 || !mesh2) {
      Utils.notify('Features not found', 'error');
      return;
    }

    const distance = mesh1.position.distanceTo(mesh2.position);
    Utils.notify(`Distance: ${distance.toFixed(3)} units`, 'success');

    console.log('[CADViewer] Distance:', distance);
  }

  /**
   * Measure angle between two lines/faces
   */
  measureAngle() {
    Utils.notify('Angle measurement - Select 2 lines in active sketch', 'info');

    // TODO: Implement angle measurement between sketch entities
    const angle = 90; // Placeholder
    Utils.notify(`Angle: ${angle}°`, 'success');
  }

  /**
   * Calculate surface area
   */
  measureArea() {
    if (!this.selectedFeatureId) {
      Utils.notify('Select a feature first', 'warning');
      return;
    }

    const mesh = this.cadObjects.get(this.selectedFeatureId);
    if (!mesh || !mesh.geometry) {
      Utils.notify('No geometry found', 'error');
      return;
    }

    // Calculate surface area from geometry
    let area = 0;
    const positions = mesh.geometry.attributes.position;
    const index = mesh.geometry.index;

    if (index) {
      for (let i = 0; i < index.count; i += 3) {
        const a = new THREE.Vector3().fromBufferAttribute(positions, index.getX(i));
        const b = new THREE.Vector3().fromBufferAttribute(positions, index.getX(i + 1));
        const c = new THREE.Vector3().fromBufferAttribute(positions, index.getX(i + 2));

        // Triangle area: 0.5 * |AB × AC|
        const ab = b.clone().sub(a);
        const ac = c.clone().sub(a);
        const cross = ab.cross(ac);
        area += cross.length() * 0.5;
      }
    }

    Utils.notify(`Surface Area: ${area.toFixed(3)} sq units`, 'success');
    console.log('[CADViewer] Surface area:', area);
  }

  /**
   * Calculate volume
   */
  measureVolume() {
    if (!this.selectedFeatureId) {
      Utils.notify('Select a feature first', 'warning');
      return;
    }

    const mesh = this.cadObjects.get(this.selectedFeatureId);
    if (!mesh || !mesh.geometry) {
      Utils.notify('No geometry found', 'error');
      return;
    }

    // Calculate volume using divergence theorem
    let volume = 0;
    const positions = mesh.geometry.attributes.position;
    const index = mesh.geometry.index;

    if (index) {
      for (let i = 0; i < index.count; i += 3) {
        const a = new THREE.Vector3().fromBufferAttribute(positions, index.getX(i));
        const b = new THREE.Vector3().fromBufferAttribute(positions, index.getX(i + 1));
        const c = new THREE.Vector3().fromBufferAttribute(positions, index.getX(i + 2));

        // Signed volume of tetrahedron with origin
        volume += a.dot(b.clone().cross(c)) / 6;
      }
    }

    volume = Math.abs(volume);

    Utils.notify(`Volume: ${volume.toFixed(3)} cubic units`, 'success');
    console.log('[CADViewer] Volume:', volume);
  }

  /**
   * Calculate center of mass
   */
  measureCenterOfMass() {
    if (!this.selectedFeatureId) {
      Utils.notify('Select a feature first', 'warning');
      return;
    }

    const mesh = this.cadObjects.get(this.selectedFeatureId);
    if (!mesh || !mesh.geometry) {
      Utils.notify('No geometry found', 'error');
      return;
    }

    // Calculate centroid (simplified - assumes uniform density)
    const positions = mesh.geometry.attributes.position;
    const center = new THREE.Vector3();
    let count = 0;

    for (let i = 0; i < positions.count; i++) {
      center.x += positions.getX(i);
      center.y += positions.getY(i);
      center.z += positions.getZ(i);
      count++;
    }

    center.divideScalar(count);

    // Apply mesh transform
    mesh.localToWorld(center);

    Utils.notify(`Center of Mass: (${center.x.toFixed(3)}, ${center.y.toFixed(3)}, ${center.z.toFixed(3)})`, 'success');

    // Add visual indicator
    const markerGeom = new THREE.SphereGeometry(0.05, 16, 16);
    const markerMat = new THREE.MeshBasicMaterial({ color: 0xFF0000 });
    const marker = new THREE.Mesh(markerGeom, markerMat);
    marker.position.copy(center);
    this.scene.add(marker);

    console.log('[CADViewer] Center of mass:', center);
  }

  // ===== File Import =====

  /**
   * Import STEP file (reuses existing DataLoader from simulation viewer)
   */
  async importSTEP(file) {
    if (!file) {
      // Show file picker
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.step,.stp,.iges,.igs';
      input.onchange = async (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
          await this.importSTEP(selectedFile);
        }
      };
      input.click();
      return;
    }

    Utils.notify('Importing STEP file...', 'info');

    try {
      // Use existing DataLoader (from simulation viewer)
      const dataLoader = new DataLoader();
      const result = await dataLoader.loadSTEP(file);

      console.log('[CADViewer] STEP import result:', result);

      // Convert loaded geometry to CAD features
      if (result.type === 'mesh') {
        if (result.parts && result.parts.length > 0) {
          // Multi-part STEP file (assembly)
          result.parts.forEach((part, index) => {
            this.addImportedPart(part.name || `ImportedPart${index + 1}`, part.geometry, part.color);
          });

          Utils.notify(`Imported ${result.parts.length} parts from STEP file`, 'success');
        } else {
          // Single part STEP file
          this.addImportedPart(
            file.name.replace(/\.(step|stp|iges|igs)$/i, ''),
            result.geometry,
            null
          );

          Utils.notify('STEP file imported successfully', 'success');
        }

        // Fit camera to show imported geometry
        if (this.engine.fitCameraToObject) {
          const allMeshes = Array.from(this.cadObjects.values());
          if (allMeshes.length > 0) {
            this.engine.fitCameraToObject(allMeshes[allMeshes.length - 1]);
          }
        }
      }

      this.updateFeatureTree();
      this.document.hasUnsavedChanges = true;

    } catch (error) {
      console.error('[CADViewer] STEP import error:', error);
      Utils.notify('Failed to import STEP: ' + error.message, 'error');
    }
  }

  /**
   * Add imported part to document and scene
   */
  addImportedPart(name, geometry, color) {
    // Create feature
    const feature = new Feature(
      Utils.generateId(),
      'imported',
      {
        name: name,
        sourceFile: 'STEP/IGES',
        color: color
      }
    );

    this.document.addFeature(feature);

    // Create mesh with color if provided
    const materialColor = color ? new THREE.Color(color[0], color[1], color[2]) : 0x808080;
    const material = new THREE.MeshStandardMaterial({
      color: materialColor,
      metalness: 0.3,
      roughness: 0.7
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name,
      imported: true
    };

    this.scene.add(mesh);
    this.cadObjects.set(feature.id, mesh);

    console.log('[CADViewer] Added imported part:', name);
  }

  /**
   * Export CAD model as STL file
   */
  exportSTL() {
    if (this.cadObjects.size === 0) {
      Utils.notify('No CAD objects to export', 'warning');
      return;
    }

    try {
      // Check if STLExporter is available
      if (typeof THREE.STLExporter === 'undefined') {
        Utils.notify('STL Exporter not loaded', 'error');
        console.error('[CADViewer] STLExporter not available');
        return;
      }

      // Collect all visible CAD meshes
      const meshes = [];
      this.cadObjects.forEach((obj, id) => {
        if (obj instanceof THREE.Mesh && obj.visible) {
          meshes.push(obj);
        }
      });

      if (meshes.length === 0) {
        Utils.notify('No visible meshes to export', 'warning');
        return;
      }

      // Create a group to hold all meshes
      const group = new THREE.Group();
      meshes.forEach(mesh => {
        // Clone the mesh to preserve the original
        const clone = mesh.clone();
        group.add(clone);
      });

      // Export to STL
      const exporter = new THREE.STLExporter();
      const stlString = exporter.parse(group, { binary: false });

      // Trigger download
      const blob = new Blob([stlString], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'cad_model.stl';
      link.click();

      Utils.notify(`STL exported successfully (${meshes.length} objects)`, 'success');
      console.log('[CADViewer] STL export complete:', meshes.length, 'objects');

    } catch (error) {
      Utils.notify('STL export failed', 'error');
      console.error('[CADViewer] STL export error:', error);
    }
  }

  /**
   * Export CAD model as OBJ file
   */
  exportOBJ() {
    if (this.cadObjects.size === 0) {
      Utils.notify('No CAD objects to export', 'warning');
      return;
    }

    try {
      // Check if OBJExporter is available
      if (typeof THREE.OBJExporter === 'undefined') {
        Utils.notify('OBJ Exporter not loaded', 'error');
        console.error('[CADViewer] OBJExporter not available');
        return;
      }

      // Collect all visible CAD meshes
      const meshes = [];
      this.cadObjects.forEach((obj, id) => {
        if (obj instanceof THREE.Mesh && obj.visible) {
          meshes.push(obj);
        }
      });

      if (meshes.length === 0) {
        Utils.notify('No visible meshes to export', 'warning');
        return;
      }

      // Create a group to hold all meshes
      const group = new THREE.Group();
      meshes.forEach(mesh => {
        // Clone the mesh to preserve the original
        const clone = mesh.clone();
        group.add(clone);
      });

      // Export to OBJ
      const exporter = new THREE.OBJExporter();
      const objString = exporter.parse(group);

      // Trigger download
      const blob = new Blob([objString], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'cad_model.obj';
      link.click();

      Utils.notify(`OBJ exported successfully (${meshes.length} objects)`, 'success');
      console.log('[CADViewer] OBJ export complete:', meshes.length, 'objects');

    } catch (error) {
      Utils.notify('OBJ export failed', 'error');
      console.error('[CADViewer] OBJ export error:', error);
    }
  }

  /**
   * Export CAD model as glTF file
   */
  exportGLTF() {
    if (this.cadObjects.size === 0) {
      Utils.notify('No CAD objects to export', 'warning');
      return;
    }

    try {
      // Check if GLTFExporter is available
      if (typeof THREE.GLTFExporter === 'undefined') {
        Utils.notify('glTF Exporter not loaded', 'error');
        console.error('[CADViewer] GLTFExporter not available');
        return;
      }

      // Collect all visible CAD meshes
      const meshes = [];
      this.cadObjects.forEach((obj, id) => {
        if (obj instanceof THREE.Mesh && obj.visible) {
          meshes.push(obj);
        }
      });

      if (meshes.length === 0) {
        Utils.notify('No visible meshes to export', 'warning');
        return;
      }

      // Create a group to hold all meshes
      const group = new THREE.Group();
      meshes.forEach(mesh => {
        // Clone the mesh to preserve the original
        const clone = mesh.clone();
        group.add(clone);
      });

      // Export to glTF (binary format)
      const exporter = new THREE.GLTFExporter();

      exporter.parse(
        group,
        (gltf) => {
          // Success callback
          const blob = new Blob([JSON.stringify(gltf, null, 2)], { type: 'application/json' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = 'cad_model.gltf';
          link.click();

          Utils.notify(`glTF exported successfully (${meshes.length} objects)`, 'success');
          console.log('[CADViewer] glTF export complete:', meshes.length, 'objects');
        },
        (error) => {
          // Error callback
          Utils.notify('glTF export failed', 'error');
          console.error('[CADViewer] glTF export error:', error);
        },
        { binary: false } // Export as .gltf (JSON) instead of .glb (binary)
      );

    } catch (error) {
      Utils.notify('glTF export failed', 'error');
      console.error('[CADViewer] glTF export error:', error);
    }
  }
}


// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CADViewer };
}
